import type { DomainNode } from "../types/standards";

export const STANDARDS_MAP: DomainNode[] = [
  {
    id: "K.CC",
    code: "CC",
    name: "Counting & Cardinality",
    grade: "K",
    clusters: [
      {
        id: "K.CC.A",
        letter: "KA",
        short: "Know number names and the count sequence.",
        standards: [
          {
            id: "K.CC.A.1",
            s: "A.1",
            desc: "Count to 100 by ones and by tens.",
          },
          {
            id: "K.CC.A.2",
            s: "A.2",
            desc: "Count forward beginning from a given number within the known sequence (instead of having to begin at 1).",
          },
          {
            id: "K.CC.A.3",
            s: "A.3",
            desc: "Write numbers from 0 to 20. Represent a number of objects with a written numeral 0-20 (with 0 representing a count of no objects).",
          },
        ],
      },
      {
        id: "K.CC.B",
        letter: "KB",
        short: "Count to tell the number of objects.",
        standards: [
          {
            id: "K.CC.B.4",
            s: "B.4",
            desc: "Understand the relationship between numbers and quantities; connect counting to cardinality.",
          },
          {
            id: "K.CC.B.5",
            s: "B.5",
            desc: "Count to answer “how many?” questions about as many as 20 things arranged in a line, a rectangular array, or a circle, or as many as 10 things in a scattered configuration; given a number from 1–20, count out that many objects.",
          },
        ],
      },
      {
        id: "K.CC.C",
        letter: "KC",
        short: "Compare numbers.",
        standards: [
          {
            id: "K.CC.C.6",
            s: "C.6",
            desc: "Identify whether the number of objects in one group is greater than, less than, or equal to the number of objects in another group, e.g., by using matching and counting strategies (include groups with up to ten objects).",
          },
          {
            id: "K.CC.C.7",
            s: "C.7",
            desc: "Compare two numbers between 1 and 10 presented as written numerals.",
          },
        ],
      },
    ],
  },
  {
    id: "K.G",
    code: "G",
    name: "Geometry",
    grade: "K",
    clusters: [
      {
        id: "K.G.A",
        letter: "KA",
        short:
          "Identify and describe shapes (squares, circles, triangles, rectangles, hexagons, cubes, cones, cylinders, and spheres).",
        standards: [
          {
            id: "K.G.A.1",
            s: "A.1",
            desc: "Describe objects in the environment using names of shapes, and describe the relative positions of these objects using terms such as above, below, beside, in front of, behind, and next to.",
          },
          {
            id: "K.G.A.2",
            s: "A.2",
            desc: "Correctly name shapes regardless of their orientations or overall size.",
          },
          {
            id: "K.G.A.3",
            s: "A.3",
            desc: "Identify shapes as two-dimensional (lying in a plane, “flat”) or three-dimensional (“solid”).",
          },
        ],
      },
      {
        id: "K.G.B",
        letter: "KB",
        short: "Analyze, compare, create, and compose shapes.",
        standards: [
          {
            id: "K.G.B.4",
            s: "B.4",
            desc: "Analyze and compare two- and three-dimensional shapes, in different sizes and orientations, using informal language to describe their similarities, differences, parts (e.g., number of sides and vertices/“corners”) and other attributes (e.g., having sides of equal length).",
          },
          {
            id: "K.G.B.5",
            s: "B.5",
            desc: "Model shapes in the world by building shapes from components (e.g., sticks and clay balls) and drawing shapes.",
          },
          {
            id: "K.G.B.6",
            s: "B.6",
            desc: "Compose simple shapes to form larger shapes. For example, “Can you join these two triangles with full sides touching to make a rectangle?”",
          },
        ],
      },
    ],
  },
  {
    id: "K.MD",
    code: "MD",
    name: "Measurement & Data",
    grade: "K",
    clusters: [
      {
        id: "K.MD.A",
        letter: "KA",
        short: "Describe and compare measurable attributes.",
        standards: [
          {
            id: "K.MD.A.1",
            s: "A.1",
            desc: "Describe measurable attributes of objects, such as length or weight. Describe several measurable attributes of a single object.",
          },
          {
            id: "K.MD.A.2",
            s: "A.2",
            desc: "Directly compare two objects with a measurable attribute in common, to see which object has “more of”/“less of” the attribute, and describe the difference. For example, directly compare the heights of two children and describe one child as taller/shorter.",
          },
        ],
      },
      {
        id: "K.MD.B",
        letter: "KB",
        short:
          "Classify objects and count the number of objects in each category.",
        standards: [
          {
            id: "K.MD.B.3",
            s: "B.3",
            desc: "Classify objects into given categories; count the numbers of objects in each category and sort the categories by count. (Limit category counts to be less than or equal to 10.)",
          },
        ],
      },
    ],
  },
  {
    id: "K.NBT",
    code: "NBT",
    name: "Number & Operations in Base Ten",
    grade: "K",
    clusters: [
      {
        id: "K.NBT.A",
        letter: "KA",
        short: "Work with numbers 11–19 to gain foundations for place value.",
        standards: [
          {
            id: "K.NBT.A.1",
            s: "A.1",
            desc: "Compose and decompose numbers from 11 to 19 into ten ones and some further ones, e.g., by using objects or drawings, and record each composition or decomposition by a drawing or equation (e.g., 18 = 10 + 8); understand that these numbers are composed of ten ones and one, two, three, four, five, six, seven, eight, or nine ones.",
          },
        ],
      },
    ],
  },
  {
    id: "K.OA",
    code: "OA",
    name: "Operations & Algebraic Thinking",
    grade: "K",
    clusters: [
      {
        id: "K.OA.A",
        letter: "KA",
        short:
          "Understand addition as putting together and adding to, and understand subtraction as taking apart and taking from.",
        standards: [
          {
            id: "K.OA.A.1",
            s: "A.1",
            desc: "Represent addition and subtraction with objects, fingers, mental images, drawings (Drawings need not show details, but should show the mathematics in the problem. (This applies wherever drawings are mentioned in the Standards.)), sounds (e.g., claps), acting out situations, verbal explanations, expressions, or equations.",
          },
          {
            id: "K.OA.A.2",
            s: "A.2",
            desc: "Solve addition and subtraction word problems, and add and subtract within 10, e.g., by using objects or drawings to represent the problem.",
          },
          {
            id: "K.OA.A.3",
            s: "A.3",
            desc: "Decompose numbers less than or equal to 10 into pairs in more than one way, e.g., by using objects or drawings, and record each decomposition by a drawing or equation (e.g., 5 = 2 + 3 and 5 = 4 + 1).",
          },
          {
            id: "K.OA.A.4",
            s: "A.4",
            desc: "For any number from 1 to 9, find the number that makes 10 when added to the given number, e.g., by using objects or drawings, and record the answer with a drawing or equation.",
          },
          {
            id: "K.OA.A.5",
            s: "A.5",
            desc: "Fluently add and subtract within 5.",
          },
        ],
      },
    ],
  },
  {
    id: "1.G",
    code: "G",
    name: "Geometry",
    grade: "1",
    clusters: [
      {
        id: "1.G.A",
        letter: "1A",
        short: "Reason with shapes and their attributes.",
        standards: [
          {
            id: "1.G.A.1",
            s: "A.1",
            desc: "Distinguish between defining attributes (e.g., triangles are closed and three-sided) versus non-defining attributes (e.g., color, orientation, overall size); build and draw shapes to possess defining attributes.",
          },
          {
            id: "1.G.A.2",
            s: "A.2",
            desc: "Compose two-dimensional shapes (rectangles, squares, trapezoids, triangles, half-circles, and quarter-circles) or three-dimensional shapes (cubes, right rectangular prisms, right circular cones, and right circular cylinders) to create a composite shape, and compose new shapes from the composite shape. (Students do not need to learn formal names such as “right rectangular prism.”)",
          },
          {
            id: "1.G.A.3",
            s: "A.3",
            desc: "Partition circles and rectangles into two and four equal shares, describe the shares using the words halves, fourths, and quarters, and use the phrases half of, fourth of, and quarter of. Describe the whole as two of, or four of the shares. Understand for these examples that decomposing into more equal shares creates smaller shares.",
          },
        ],
      },
    ],
  },
  {
    id: "1.MD",
    code: "MD",
    name: "Measurement & Data",
    grade: "1",
    clusters: [
      {
        id: "1.MD.A",
        letter: "1A",
        short: "Measure lengths indirectly and by iterating length units.",
        standards: [
          {
            id: "1.MD.A.1",
            s: "A.1",
            desc: "Order three objects by length; compare the lengths of two objects indirectly by using a third object.",
          },
          {
            id: "1.MD.A.2",
            s: "A.2",
            desc: "Express the length of an object as a whole number of length units, by laying multiple copies of a shorter object (the length unit) end to end; understand that the length measurement of an object is the number of same-size length units that span it with no gaps or overlaps. Limit to contexts where the object being measured is spanned by a whole number of length units with no gaps or overlaps.",
          },
        ],
      },
      {
        id: "1.MD.B",
        letter: "1B",
        short: "Tell and write time.",
        standards: [
          {
            id: "1.MD.B.3",
            s: "B.3",
            desc: "Tell and write time in hours and half-hours using analog and digital clocks.",
          },
        ],
      },
      {
        id: "1.MD.C",
        letter: "1C",
        short: "Represent and interpret data.",
        standards: [
          {
            id: "1.MD.C.4",
            s: "C.4",
            desc: "Organize, represent, and interpret data with up to three categories; ask and answer questions about the total number of data points, how many in each category, and how many more or less are in one category than in another.",
          },
        ],
      },
    ],
  },
  {
    id: "1.NBT",
    code: "NBT",
    name: "Number & Operations in Base Ten",
    grade: "1",
    clusters: [
      {
        id: "1.NBT.A",
        letter: "1A",
        short: "Extend the counting sequence.",
        standards: [
          {
            id: "1.NBT.A.1",
            s: "A.1",
            desc: "Count to 120, starting at any number less than 120. In this range, read and write numerals and represent a number of objects with a written numeral.",
          },
        ],
      },
      {
        id: "1.NBT.B",
        letter: "1B",
        short: "Understand place value.",
        standards: [
          {
            id: "1.NBT.B.2",
            s: "B.2",
            desc: "Understand that the two digits of a two-digit number represent amounts of tens and ones. Understand the following as special cases:",
          },
          {
            id: "1.NBT.B.3",
            s: "B.3",
            desc: "Compare two two-digit numbers based on meanings of the tens and ones digits, recording the results of comparisons with the symbols >, =, and <.",
          },
        ],
      },
      {
        id: "1.NBT.C",
        letter: "1C",
        short:
          "Use place value understanding and properties of operations to add and subtract.",
        standards: [
          {
            id: "1.NBT.C.4",
            s: "C.4",
            desc: "Add within 100, including adding a two-digit number and a one-digit number, and adding a two-digit number and a multiple of 10, using concrete models or drawings and strategies based on place value, properties of operations, and/or the relationship between addition and subtraction; relate the strategy to a written method and explain the reasoning used. Understand that in adding two-digit numbers, one adds tens and tens, ones and ones; and sometimes it is necessary to compose a ten.",
          },
          {
            id: "1.NBT.C.5",
            s: "C.5",
            desc: "Given a two-digit number, mentally find 10 more or 10 less than the number, without having to count; explain the reasoning used.",
          },
          {
            id: "1.NBT.C.6",
            s: "C.6",
            desc: "Subtract multiples of 10 in the range 10-90 from multiples of 10 in the range 10-90 (positive or zero differences), using concrete models or drawings and strategies based on place value, properties of operations, and/or the relationship between addition and subtraction; relate the strategy to a written method and explain the reasoning used.",
          },
        ],
      },
    ],
  },
  {
    id: "1.OA",
    code: "OA",
    name: "Operations & Algebraic Thinking",
    grade: "1",
    clusters: [
      {
        id: "1.OA.A",
        letter: "1A",
        short:
          "Represent and solve problems involving addition and subtraction.",
        standards: [
          {
            id: "1.OA.A.1",
            s: "A.1",
            desc: "Use addition and subtraction within 20 to solve word problems involving situations of adding to, taking from, putting together, taking apart, and comparing, with unknowns in all positions, e.g., by using objects, drawings, and equations with a symbol for the unknown number to represent the problem.",
          },
          {
            id: "1.OA.A.2",
            s: "A.2",
            desc: "Solve word problems that call for addition of three whole numbers whose sum is less than or equal to 20, e.g., by using objects, drawings, and equations with a symbol for the unknown number to represent the problem.",
          },
        ],
      },
      {
        id: "1.OA.B",
        letter: "1B",
        short:
          "Understand and apply properties of operations and the relationship between addition and subtraction.",
        standards: [
          {
            id: "1.OA.B.3",
            s: "B.3",
            desc: "Apply properties of operations as strategies to add and subtract. (Students need not use formal terms for these properties.) Examples: If 8 + 3 = 11 is known, then 3 + 8 = 11 is also known. (Commutative property of addition.) To add 2 + 6 + 4, the second two numbers can be added to make a ten, so 2 + 6 + 4 = 2 + 10 = 12. (Associative property of addition.)",
          },
          {
            id: "1.OA.B.4",
            s: "B.4",
            desc: "Understand subtraction as an unknown-addend problem. For example, subtract 10 – 8 by finding the number that makes 10 when added to 8.",
          },
        ],
      },
      {
        id: "1.OA.C",
        letter: "1C",
        short: "Add and subtract within 20.",
        standards: [
          {
            id: "1.OA.C.5",
            s: "C.5",
            desc: "Relate counting to addition and subtraction (e.g., by counting on 2 to add 2).",
          },
          {
            id: "1.OA.C.6",
            s: "C.6",
            desc: "Add and subtract within 20, demonstrating fluency for addition and subtraction within 10. Use strategies such as counting on; making ten (e.g., 8 + 6 = 8 + 2 + 4 = 10 + 4 = 14); decomposing a number leading to a ten (e.g., 13 – 4 = 13 – 3 – 1 = 10 – 1 = 9); using the relationship between addition and subtraction (e.g., knowing that 8 + 4 = 12, one knows 12 – 8 = 4); and creating equivalent but easier or known sums (e.g., adding 6 + 7 by creating the known equivalent 6 + 6 + 1 = 12 + 1 = 13).",
          },
        ],
      },
      {
        id: "1.OA.D",
        letter: "1D",
        short: "Work with addition and subtraction equations.",
        standards: [
          {
            id: "1.OA.D.7",
            s: "D.7",
            desc: "Understand the meaning of the equal sign, and determine if equations involving addition and subtraction are true or false. For example, which of the following equations are true and which are false? 6 = 6, 7 = 8 – 1, 5 + 2 = 2 + 5, 4 + 1 = 5 + 2.",
          },
          {
            id: "1.OA.D.8",
            s: "D.8",
            desc: "Determine the unknown whole number in an addition or subtraction equation relating to three whole numbers. For example, determine the unknown number that makes the equation true in each of the equations 8 + ? = 11, 5 = �� – 3, 6 + 6 = ��.",
          },
        ],
      },
    ],
  },
  {
    id: "2.G",
    code: "G",
    name: "Geometry",
    grade: "2",
    clusters: [
      {
        id: "2.G.A",
        letter: "2A",
        short: "Reason with shapes and their attributes.",
        standards: [
          {
            id: "2.G.A.1",
            s: "A.1",
            desc: "Recognize and draw shapes having specified attributes, such as a given number of angles or a given number of equal faces. (Sizes are compared directly or visually, not compared by measuring.) Identify triangles, quadrilaterals, pentagons, hexagons, and cubes.",
          },
          {
            id: "2.G.A.2",
            s: "A.2",
            desc: "Partition a rectangle into rows and columns of same-size squares and count to find the total number of them.",
          },
          {
            id: "2.G.A.3",
            s: "A.3",
            desc: "Partition circles and rectangles into two, three, or four equal shares, describe the shares using the words halves, thirds, half of, a third of, etc., and describe the whole as two halves, three thirds, four fourths. Recognize that equal shares of identical wholes need not have the same shape.",
          },
        ],
      },
    ],
  },
  {
    id: "2.MD",
    code: "MD",
    name: "Measurement & Data",
    grade: "2",
    clusters: [
      {
        id: "2.MD.A",
        letter: "2A",
        short: "Measure and estimate lengths in standard units.",
        standards: [
          {
            id: "2.MD.A.1",
            s: "A.1",
            desc: "Measure the length of an object by selecting and using appropriate tools such as rulers, yardsticks, meter sticks, and measuring tapes.",
          },
          {
            id: "2.MD.A.2",
            s: "A.2",
            desc: "Measure the length of an object twice, using length units of different lengths for the two measurements; describe how the two measurements relate to the size of the unit chosen.",
          },
          {
            id: "2.MD.A.3",
            s: "A.3",
            desc: "Estimate lengths using units of inches, feet, centimeters, and meters.",
          },
          {
            id: "2.MD.A.4",
            s: "A.4",
            desc: "Measure to determine how much longer one object is than another, expressing the length difference in terms of a standard length unit.",
          },
        ],
      },
      {
        id: "2.MD.B",
        letter: "2B",
        short: "Relate addition and subtraction to length.",
        standards: [
          {
            id: "2.MD.B.5",
            s: "B.5",
            desc: "Use addition and subtraction within 100 to solve word problems involving lengths that are given in the same units, e.g., by using drawings (such as drawings of rulers) and equations with a symbol for the unknown number to represent the problem.",
          },
          {
            id: "2.MD.B.6",
            s: "B.6",
            desc: "Represent whole numbers as lengths from 0 on a number line diagram with equally spaced points corresponding to the numbers 0, 1, 2, ..., and represent whole-number sums and differences within 100 on a number line diagram.",
          },
        ],
      },
      {
        id: "2.MD.C",
        letter: "2C",
        short: "Work with time and money.",
        standards: [
          {
            id: "2.MD.C.7",
            s: "C.7",
            desc: "Tell and write time from analog and digital clocks to the nearest five minutes, using a.m. and p.m.",
          },
          {
            id: "2.MD.C.8",
            s: "C.8",
            desc: "Solve word problems involving dollar bills, quarters, dimes, nickels, and pennies, using $ and ¢ symbols appropriately. Example: If you have 2 dimes and 3 pennies, how many cents do you have?",
          },
        ],
      },
      {
        id: "2.MD.D",
        letter: "2D",
        short: "Represent and interpret data.",
        standards: [
          {
            id: "2.MD.D.10",
            s: "D.10",
            desc: "Draw a picture graph and a bar graph (with single-unit scale) to represent a data set with up to four categories. Solve simple put together, take-apart, and compare problems  using information presented in a bar graph.",
          },
          {
            id: "2.MD.D.9",
            s: "D.9",
            desc: "Generate measurement data by measuring lengths of several objects to the nearest whole unit, or by making repeated measurements of the same object. Show the measurements by making a line plot, where the horizontal scale is marked off in whole-number units.",
          },
        ],
      },
    ],
  },
  {
    id: "2.NBT",
    code: "NBT",
    name: "Number & Operations in Base Ten",
    grade: "2",
    clusters: [
      {
        id: "2.NBT.A",
        letter: "2A",
        short: "Understand place value.",
        standards: [
          {
            id: "2.NBT.A.1",
            s: "A.1",
            desc: "Understand that the three digits of a three-digit number represent amounts of hundreds, tens, and ones; e.g., 706 equals 7 hundreds, 0 tens, and 6 ones. Understand the following as special cases:",
          },
          {
            id: "2.NBT.A.2",
            s: "A.2",
            desc: "Count within 1000; skip-count by 5s, 10s, and 100s.",
          },
          {
            id: "2.NBT.A.3",
            s: "A.3",
            desc: "Read and write numbers to 1000 using base-ten numerals, number names, and expanded form.",
          },
          {
            id: "2.NBT.A.4",
            s: "A.4",
            desc: "Compare two three-digit numbers based on meanings of the hundreds, tens, and ones digits, using >, =, and < symbols to record the results of comparisons.",
          },
        ],
      },
      {
        id: "2.NBT.B",
        letter: "2B",
        short:
          "Use place value understanding and properties of operations to add and subtract.",
        standards: [
          {
            id: "2.NBT.B.5",
            s: "B.5",
            desc: "Fluently add and subtract within 100 using strategies based on place value, properties of operations, and/or the relationship between addition and subtraction.",
          },
          {
            id: "2.NBT.B.6",
            s: "B.6",
            desc: "Add up to four two-digit numbers using strategies based on place value and properties of operations.",
          },
          {
            id: "2.NBT.B.7",
            s: "B.7",
            desc: "Add and subtract within 1000, using concrete models or drawings and strategies based on place value, properties of operations, and/or the relationship between addition and subtraction; relate the strategy to a written method. Understand that in adding or subtracting three-digit numbers, one adds or subtracts hundreds and hundreds, tens and tens, ones and ones; and sometimes it is necessary to compose or decompose tens or hundreds.",
          },
          {
            id: "2.NBT.B.8",
            s: "B.8",
            desc: "Mentally add 10 or 100 to a given number 100–900, and mentally subtract 10 or 100 from a given number 100–900.",
          },
          {
            id: "2.NBT.B.9",
            s: "B.9",
            desc: "Explain why addition and subtraction strategies work, using place value and the properties of operations. (Explanations may be supported by drawings or objects.)",
          },
        ],
      },
    ],
  },
  {
    id: "2.OA",
    code: "OA",
    name: "Operations & Algebraic Thinking",
    grade: "2",
    clusters: [
      {
        id: "2.OA.A",
        letter: "2A",
        short:
          "Represent and solve problems involving addition and subtraction.",
        standards: [
          {
            id: "2.OA.A.1",
            s: "A.1",
            desc: "Use addition and subtraction within 100 to solve one- and two-step word problems involving situations of adding to, taking from, putting together, taking apart, and comparing, with unknowns in all positions, e.g., by using drawings and equations with a symbol for the unknown number to represent the problem.",
          },
        ],
      },
      {
        id: "2.OA.B",
        letter: "2B",
        short: "Add and subtract within 20.",
        standards: [
          {
            id: "2.OA.B.2",
            s: "B.2",
            desc: "Fluently add and subtract within 20 using mental strategies. (See standard 1.OA.6 for a list of mental strategies.) By end of Grade 2, know from memory all sums of two one-digit numbers.",
          },
        ],
      },
      {
        id: "2.OA.C",
        letter: "2C",
        short:
          "Work with equal groups of objects to gain foundations for multiplication.",
        standards: [
          {
            id: "2.OA.C.3",
            s: "C.3",
            desc: "Determine whether a group of objects (up to 20) has an odd or even number of members, e.g., by pairing objects or counting them by 2s; write an equation to express an even number as a sum of two equal addends.",
          },
          {
            id: "2.OA.C.4",
            s: "C.4",
            desc: "Use addition to find the total number of objects arranged in rectangular arrays with up to 5 rows and up to 5 columns; write an equation to express the total as a sum of equal addends.",
          },
        ],
      },
    ],
  },
  {
    id: "3.G",
    code: "G",
    name: "Geometry",
    grade: "3",
    clusters: [
      {
        id: "3.G.A",
        letter: "3A",
        short: "Reason with shapes and their attributes.",
        standards: [
          {
            id: "3.G.A.1",
            s: "A.1",
            desc: "Understand that shapes in different categories (e.g., rhombuses, rectangles, and others) may share attributes (e.g., having four sides), and that the shared attributes can define a larger category (e.g., quadrilaterals). Recognize rhombuses, rectangles, and squares as examples of quadrilaterals, and draw examples of quadrilaterals that do not belong to any of these subcategories.",
          },
          {
            id: "3.G.A.2",
            s: "A.2",
            desc: "Partition shapes into parts with equal areas. Express the area of each part as a unit fraction of the whole. For example, partition a shape into 4 parts with equal area, and describe the area of each part as 1/4 of the area of the shape.",
          },
        ],
      },
    ],
  },
  {
    id: "3.MD",
    code: "MD",
    name: "Measurement & Data",
    grade: "3",
    clusters: [
      {
        id: "3.MD.A",
        letter: "3A",
        short:
          "Solve problems involving measurement and estimation of intervals of time, liquid volumes, and masses of objects.",
        standards: [
          {
            id: "3.MD.A.1",
            s: "A.1",
            desc: "Tell and write time to the nearest minute and measure time intervals in minutes. Solve word problems involving addition and subtraction of time intervals in minutes, e.g., by representing the problem on a number line diagram.",
          },
          {
            id: "3.MD.A.2",
            s: "A.2",
            desc: "Measure and estimate liquid volumes and masses of objects using standard units of grams (g), kilograms (kg), and liters (l). (Excludes compound units such as cm^(3) and finding the geometric volume of a container.) Add, subtract, multiply, or divide to solve one-step word problems involving masses or volumes that are given in the same units, e.g., by using drawings (such as a beaker with a measurement scale) to represent the problem. [Excludes multiplicative comparison problems (problems involving notions of “times as much”)]",
          },
        ],
      },
      {
        id: "3.MD.B",
        letter: "3B",
        short: "Represent and interpret data.",
        standards: [
          {
            id: "3.MD.B.3",
            s: "B.3",
            desc: "Draw a scaled picture graph and a scaled bar graph to represent a data set with several categories. Solve one- and two-step “how many more” and “how many less” problems using information presented in scaled bar graphs. For example, draw a bar graph in which each square in the bar graph might represent 5 pets.",
          },
          {
            id: "3.MD.B.4",
            s: "B.4",
            desc: "Generate measurement data by measuring lengths using rulers marked with halves and fourths of an inch. Show the data by making a line plot, where the horizontal scale is marked off in appropriate units— whole numbers, halves, or quarters.",
          },
        ],
      },
      {
        id: "3.MD.C",
        letter: "3C",
        short:
          "Geometric measurement: understand concepts of area and relate area to multiplication and to addition.",
        standards: [
          {
            id: "3.MD.C.5",
            s: "C.5",
            desc: "Recognize area as an attribute of plane figures and understand concepts of area measurement.",
          },
          {
            id: "3.MD.C.6",
            s: "C.6",
            desc: "Measure areas by counting unit squares (square cm, square m, square in, square ft, and improvised units).",
          },
          {
            id: "3.MD.C.7",
            s: "C.7",
            desc: "Relate area to the operations of multiplication and addition.",
          },
        ],
      },
      {
        id: "3.MD.D",
        letter: "3D",
        short:
          "Geometric measurement: recognize perimeter as an attribute of plane figures and distinguish between linear and area measures.",
        standards: [
          {
            id: "3.MD.D.8",
            s: "D.8",
            desc: "Solve real world and mathematical problems involving perimeters of polygons, including finding the perimeter given the side lengths, finding an unknown side length, and exhibiting rectangles with the same perimeter and different areas or with the same area and different perimeters.",
          },
        ],
      },
    ],
  },
  {
    id: "3.NBT",
    code: "NBT",
    name: "Number & Operations in Base Ten",
    grade: "3",
    clusters: [
      {
        id: "3.NBT.A",
        letter: "3A",
        short:
          "Use place value understanding and properties of operations to perform multi-digit arithmetic. (A range of algorithms may be used.)",
        standards: [
          {
            id: "3.NBT.A.1",
            s: "A.1",
            desc: "Use place value understanding to round whole numbers to the nearest 10 or 100.",
          },
          {
            id: "3.NBT.A.2",
            s: "A.2",
            desc: "Fluently add and subtract within 1000 using strategies and algorithms based on place value, properties of operations, and/or the relationship between addition and subtraction.",
          },
          {
            id: "3.NBT.A.3",
            s: "A.3",
            desc: "Multiply one-digit whole numbers by multiples of 10 in the range 10–90 (e.g., 9 × 80, 5 × 60) using strategies based on place value and properties of operations.",
          },
        ],
      },
    ],
  },
  {
    id: "3.NF",
    code: "NF",
    name: "Number & Operations - Fractions",
    grade: "3",
    clusters: [
      {
        id: "3.NF.A",
        letter: "3A",
        short: "Develop Understanding of fractions as numbers.",
        standards: [
          {
            id: "3.NF.A.1",
            s: "A.1",
            desc: "Understand a fraction 1/b as the quantity formed by 1 part when a whole is partitioned into b equal parts; understand a fraction a/b as the quantity formed by a parts of size 1/b.",
          },
          {
            id: "3.NF.A.2",
            s: "A.2",
            desc: "Understand a fraction as a number on the number line; represent fractions on a number line diagram.",
          },
          {
            id: "3.NF.A.3",
            s: "A.3",
            desc: "Explain equivalence of fractions in special cases, and compare fractions by reasoning about their size.",
          },
        ],
      },
    ],
  },
  {
    id: "3.OA",
    code: "OA",
    name: "Operations & Algebraic Thinking",
    grade: "3",
    clusters: [
      {
        id: "3.OA.A",
        letter: "3A",
        short:
          "Represent and solve problems involving multiplication and division.",
        standards: [
          {
            id: "3.OA.A.1",
            s: "A.1",
            desc: "Interpret products of whole numbers, e.g., interpret 5 × 7 as the total number of objects in 5 groups of 7 objects each. For example, describe a context in which a total number of objects can be expressed as 5 × 7.",
          },
          {
            id: "3.OA.A.2",
            s: "A.2",
            desc: "Interpret whole-number quotients of whole numbers, e.g., interpret 56 ÷ 8 as the number of objects in each share when 56 objects are partitioned equally into 8 shares, or as a number of shares when 56 objects are partitioned into equal shares of 8 objects each. For example, describe a context in which a number of shares or a number of groups can be expressed as 56 ÷ 8.",
          },
          {
            id: "3.OA.A.3",
            s: "A.3",
            desc: "Use multiplication and division within 100 to solve word problems in situations involving equal groups, arrays, and measurement quantities, e.g., by using drawings and equations with a symbol for the unknown number to represent the problem.",
          },
          {
            id: "3.OA.A.4",
            s: "A.4",
            desc: "Determine the unknown whole number in a multiplication or division equation relating three whole numbers. For example, determine the unknown number that makes the equation true in each of the equations 8 × ? = 48, 5 = �� ÷ 3, 6 × 6 = ?.",
          },
        ],
      },
      {
        id: "3.OA.B",
        letter: "3B",
        short:
          "Understand properties of multiplication and the relationship between multiplication and division.",
        standards: [
          {
            id: "3.OA.B.5",
            s: "B.5",
            desc: "Apply properties of operations as strategies to multiply and divide. (Students need not use formal terms for these properties.) ^( )Examples: If 6 × 4 = 24 is known, then 4 × 6 = 24 is also known. (Commutative property of multiplication.) 3 × 5 × 2 can be found by 3 × 5 = 15, then 15 × 2 = 30, or by 5 × 2 = 10, then 3 × 10 = 30. (Associative property of multiplication.) Knowing that 8 × 5 = 40 and 8 × 2 = 16, one can find 8 × 7 as 8 × (5 + 2) = (8 × 5) + (8 × 2) = 40 + 16 = 56. (Distributive property.)",
          },
          {
            id: "3.OA.B.6",
            s: "B.6",
            desc: "Understand division as an unknown-factor problem. For example, find 32 ÷ 8 by finding the number that makes 32 when multiplied by 8.",
          },
        ],
      },
      {
        id: "3.OA.C",
        letter: "3C",
        short: "Multiply and divide within 100.",
        standards: [
          {
            id: "3.OA.C.7",
            s: "C.7",
            desc: "Fluently multiply and divide within 100, using strategies such as the relationship between multiplication and division (e.g., knowing that 8 × 5 = 40, one knows 40 ÷ 5 = 8) or properties of operations. By the end of Grade 3, know from memory all products of two one-digit numbers.",
          },
        ],
      },
      {
        id: "3.OA.D",
        letter: "3D",
        short:
          "Solve problems involving the four operations, and identify and explain patterns in arithmetic.",
        standards: [
          {
            id: "3.OA.D.8",
            s: "D.8",
            desc: "Solve two-step word problems using the four operations. Represent these problems using equations with a letter standing for the unknown quantity. Assess the reasonableness of answers using mental computation and estimation strategies including rounding. (This standard is limited to problems posed with whole numbers and having whole number answers; students should know how to perform operations in the conventional order when there are no parentheses to specify a particular order (Order of Operations).)",
          },
          {
            id: "3.OA.D.9",
            s: "D.9",
            desc: "Identify arithmetic patterns (including patterns in the addition table or multiplication table), and explain them using properties of operations. For example, observe that 4 times a number is always even, and explain why 4 times a number can be decomposed into two equal addends.",
          },
        ],
      },
    ],
  },
  {
    id: "4.G",
    code: "G",
    name: "Geometry",
    grade: "4",
    clusters: [
      {
        id: "4.G.A",
        letter: "4A",
        short:
          "Draw and identify lines and angles, and classify shapes by properties of their lines and angles.",
        standards: [
          {
            id: "4.G.A.1",
            s: "A.1",
            desc: "Draw points, lines, line segments, rays, angles (right, acute, obtuse), and perpendicular and parallel lines. Identify these in two-dimensional figures.",
          },
          {
            id: "4.G.A.2",
            s: "A.2",
            desc: "Classify two-dimensional figures based on the presence or absence of parallel or perpendicular lines, or the presence or absence of angles of a specified size. Recognize right triangles as a category, and identify right triangles.",
          },
          {
            id: "4.G.A.3",
            s: "A.3",
            desc: "Recognize a line of symmetry for a two-dimensional figure as a line across the figure such that the figure can be folded along the line into matching parts. Identify line-symmetric figures and draw lines of symmetry.",
          },
        ],
      },
    ],
  },
  {
    id: "4.MD",
    code: "MD",
    name: "Measurement & Data",
    grade: "4",
    clusters: [
      {
        id: "4.MD.A",
        letter: "4A",
        short:
          "Solve problems involving measurement and conversion of measurements from a larger unit to a smaller unit.",
        standards: [
          {
            id: "4.MD.A.1",
            s: "A.1",
            desc: "Know relative sizes of measurement units within one system of units including km, m, cm; kg, g; lb, oz.; l, ml; hr, min, sec. Within a single system of measurement, express measurements in a larger unit in terms of a smaller unit. Record measurement equivalents in a two column table. For example, know that 1 ft is 12 times as long as 1 in. Express the length of a 4 ft snake as 48 in. Generate a conversion table for feet and inches listing the number pairs (1, 12), (2, 24), (3, 36), ...",
          },
          {
            id: "4.MD.A.2",
            s: "A.2",
            desc: "Use the four operations to solve word problems involving distances, intervals of time, liquid volumes, masses of objects, and money, including problems involving simple fractions or decimals, and problems that require expressing measurements given in a larger unit in terms of a smaller unit. Represent measurement quantities using diagrams such as number line diagrams that feature a measurement scale.",
          },
          {
            id: "4.MD.A.3",
            s: "A.3",
            desc: "Apply the area and perimeter formulas for rectangles in real world and mathematical problems. For example, find the width of a rectangular room given the area of the flooring and the length, by viewing the area formula as a multiplication equation with an unknown factor.",
          },
        ],
      },
      {
        id: "4.MD.B",
        letter: "4B",
        short: "Represent and interpret data.",
        standards: [
          {
            id: "4.MD.B.4",
            s: "B.4",
            desc: "Make a line plot to display a data set of measurements in fractions of a unit (1/2, 1/4, 1/8). Solve problems involving addition and subtraction of fractions by using information presented in line plots. For example, from a line plot find and interpret the difference in length between the longest and shortest specimens in an insect collection.",
          },
        ],
      },
      {
        id: "4.MD.C",
        letter: "4C",
        short:
          "Geometric measurement: understand concepts of angle and measure angles.",
        standards: [
          {
            id: "4.MD.C.5",
            s: "C.5",
            desc: "Recognize angles as geometric shapes that are formed wherever two rays share a common endpoint, and understand concepts of angle measurement:",
          },
          {
            id: "4.MD.C.6",
            s: "C.6",
            desc: "Measure angles in whole-number degrees using a protractor. Sketch angles of specified measure.",
          },
          {
            id: "4.MD.C.7",
            s: "C.7",
            desc: "Recognize angle measure as additive. When an angle is decomposed into non-overlapping parts, the angle measure of the whole is the sum of the angle measures of the parts. Solve addition and subtraction problems to find unknown angles on a diagram in real world and mathematical problems, e.g., by using an equation with a symbol for the unknown angle measure.",
          },
        ],
      },
    ],
  },
  {
    id: "4.NBT",
    code: "NBT",
    name: "Number & Operations in Base Ten",
    grade: "4",
    clusters: [
      {
        id: "4.NBT.A",
        letter: "4A",
        short:
          "Generalize place value understanding for multi-digit whole numbers.",
        standards: [
          {
            id: "4.NBT.A.1",
            s: "A.1",
            desc: "Recognize that in a multi-digit whole number, a digit in one place represents ten times what it represents in the place to its right. For example, recognize that 700 ÷ 70 = 10 by applying concepts of place value and division.",
          },
          {
            id: "4.NBT.A.2",
            s: "A.2",
            desc: "Read and write multi-digit whole numbers using base-ten numerals, number names, and expanded form. Compare two multi-digit numbers based on meanings of the digits in each place, using >, =, and < symbols to record the results of comparisons.",
          },
          {
            id: "4.NBT.A.3",
            s: "A.3",
            desc: "Use place value understanding to round multi-digit whole numbers to any place.",
          },
        ],
      },
      {
        id: "4.NBT.B",
        letter: "4B",
        short:
          "Use place value understanding and properties of operations to perform multi-digit arithmetic.",
        standards: [
          {
            id: "4.NBT.B.4",
            s: "B.4",
            desc: "Fluently add and subtract multi-digit whole numbers using the standard algorithm.",
          },
          {
            id: "4.NBT.B.5",
            s: "B.5",
            desc: "Multiply a whole number of up to four digits by a one-digit whole number, and multiply two two-digit numbers, using strategies based on place value and the properties of operations. Illustrate and explain the calculation by using equations, rectangular arrays, and/or area models.",
          },
          {
            id: "4.NBT.B.6",
            s: "B.6",
            desc: "Find whole-number quotients and remainders with up to four-digit dividends and one-digit divisors, using strategies based on place value, the properties of operations, and/or the relationship between multiplication and division. Illustrate and explain the calculation by using equations, rectangular arrays, and/or area models.",
          },
        ],
      },
    ],
  },
  {
    id: "4.NF",
    code: "NF",
    name: "Number & Operations - Fractions",
    grade: "4",
    clusters: [
      {
        id: "4.NF.A",
        letter: "4A",
        short: "Extend understanding of fraction equivalence and ordering.",
        standards: [
          {
            id: "4.NF.A.1",
            s: "A.1",
            desc: "Explain why a fraction a/b is equivalent to a fraction (n × a)/(n × b) by using visual fraction models, with attention to how the number and size of the parts differ even though the two fractions themselves are the same size. Use this principle to recognize and generate equivalent fractions.",
          },
          {
            id: "4.NF.A.2",
            s: "A.2",
            desc: "Compare two fractions with different numerators and different denominators, e.g., by creating common denominators or numerators, or by comparing to a benchmark fraction such as 1/2. Recognize that comparisons are valid only when the two fractions refer to the same whole. Record the results of comparisons with symbols >, =, or <, and justify the conclusions, e.g., by using a visual fraction model.",
          },
        ],
      },
      {
        id: "4.NF.B",
        letter: "4B",
        short:
          "Build fractions from unit fractions by applying and extending previous understandings of operations on whole numbers.",
        standards: [
          {
            id: "4.NF.B.3",
            s: "B.3",
            desc: "Understand a fraction a/b with a > 1 as a sum of fractions 1/b.",
          },
          {
            id: "4.NF.B.4",
            s: "B.4",
            desc: "Apply and extend previous understandings of multiplication to multiply a fraction by a whole number.",
          },
        ],
      },
      {
        id: "4.NF.C",
        letter: "4C",
        short:
          "Understand decimal notation for fractions, and compare decimal fractions.",
        standards: [
          {
            id: "4.NF.C.5",
            s: "C.5",
            desc: "Express a fraction with denominator 10 as an equivalent fraction with denominator 100, and use this technique to add two fractions with respective denominators 10 and 100. (Students who can generate equivalent fractions can develop strategies for adding fractions with unlike denominators in general. But addition and subtraction with unlike denominators in general is not a requirement at this grade.) For example, express 3/10 as 30/100, and add 3/10 + 4/100 = 34/100.",
          },
          {
            id: "4.NF.C.6",
            s: "C.6",
            desc: "Use decimal notation for fractions with denominators 10 or 100. For example, rewrite 0.62 as 62/100; describe a length as 0.62 meters; locate 0.62 on a number line diagram.",
          },
          {
            id: "4.NF.C.7",
            s: "C.7",
            desc: "Compare two decimals to hundredths by reasoning about their size. Recognize that comparisons are valid only when the two decimals refer to the same whole. Record the results of comparisons with the symbols >, =, or <, and justify the conclusions, e.g., by using a visual model.",
          },
        ],
      },
    ],
  },
  {
    id: "4.OA",
    code: "OA",
    name: "Operations & Algebraic Thinking",
    grade: "4",
    clusters: [
      {
        id: "4.OA.A",
        letter: "4A",
        short: "Use the four operations with whole numbers to solve problems.",
        standards: [
          {
            id: "4.OA.A.1",
            s: "A.1",
            desc: "Interpret a multiplication equation as a comparison, e.g., interpret 35 = 5 × 7 as a statement that 35 is 5 times as many as 7 and 7 times as many as 5. Represent verbal statements of multiplicative comparisons as multiplication equations.",
          },
          {
            id: "4.OA.A.2",
            s: "A.2",
            desc: "Multiply or divide to solve word problems involving multiplicative comparison, e.g., by using drawings and equations with a symbol for the unknown number to represent the problem, distinguishing multiplicative comparison from additive comparison.",
          },
          {
            id: "4.OA.A.3",
            s: "A.3",
            desc: "Solve multistep word problems posed with whole numbers and having whole-number answers using the four operations, including problems in which remainders must be interpreted. Represent these problems using equations with a letter standing for the unknown quantity. Assess the reasonableness of answers using mental computation and estimation strategies including rounding.",
          },
        ],
      },
      {
        id: "4.OA.B",
        letter: "4B",
        short: "Gain familiarity with factors and multiples.",
        standards: [
          {
            id: "4.OA.B.4",
            s: "B.4",
            desc: "Find all factor pairs for a whole number in the range 1–100. Recognize that a whole number is a multiple of each of its factors. Determine whether a given whole number in the range 1–100 is a multiple of a given one-digit number. Determine whether a given whole number in the range 1–100 is prime or composite.",
          },
        ],
      },
      {
        id: "4.OA.C",
        letter: "4C",
        short: "Generate and analyze patterns.",
        standards: [
          {
            id: "4.OA.C.5",
            s: "C.5",
            desc: "Generate a number or shape pattern that follows a given rule. Identify apparent features of the pattern that were not explicit in the rule itself. For example, given the rule “Add 3” and the starting number 1, generate terms in the resulting sequence and observe that the terms appear to alternate between odd and even numbers. Explain informally why the numbers will continue to alternate in this way.",
          },
        ],
      },
    ],
  },
  {
    id: "5.G",
    code: "G",
    name: "Geometry",
    grade: "5",
    clusters: [
      {
        id: "5.G.A",
        letter: "5A",
        short:
          "Graph points on the coordinate plane to solve real-world and mathematical problems.",
        standards: [
          {
            id: "5.G.A.1",
            s: "A.1",
            desc: "Use a pair of perpendicular number lines, called axes, to define a coordinate system, with the intersection of the lines (the origin) arranged to coincide with the 0 on each line and a given point in the plane located by using an ordered pair of numbers, called its coordinates. Understand that the first number indicates how far to travel from the origin in the direction of one axis, and the second number indicates how far to travel in the direction of the second axis, with the convention that the names of the two axes and the coordinates correspond (e.g., x-axis and x-coordinate, y-axis and y-coordinate).",
          },
          {
            id: "5.G.A.2",
            s: "A.2",
            desc: "Represent real world and mathematical problems by graphing points in the first quadrant of the coordinate plane, and interpret coordinate values of points in the context of the situation.",
          },
        ],
      },
      {
        id: "5.G.B",
        letter: "5B",
        short:
          "Classify two-dimensional figures into categories based on their properties.",
        standards: [
          {
            id: "5.G.B.3",
            s: "B.3",
            desc: "Understand that attributes belonging to a category of two-dimensional figures also belong to all subcategories of that category. For example, all rectangles have four right angles and squares are rectangles, so all squares have four right angles.",
          },
          {
            id: "5.G.B.4",
            s: "B.4",
            desc: "Classify two-dimensional figures in a hierarchy based on properties.",
          },
        ],
      },
    ],
  },
  {
    id: "5.MD",
    code: "MD",
    name: "Measurement & Data",
    grade: "5",
    clusters: [
      {
        id: "5.MD.A",
        letter: "5A",
        short:
          "Convert like measurement units within a given measurement system.",
        standards: [
          {
            id: "5.MD.A.1",
            s: "A.1",
            desc: "Convert among different-sized standard measurement units within a given measurement system (e.g., convert 5 cm to 0.05 m), and use these conversions in solving multi-step, real world problems.",
          },
        ],
      },
      {
        id: "5.MD.B",
        letter: "5B",
        short: "Represent and interpret data.",
        standards: [
          {
            id: "5.MD.B.2",
            s: "B.2",
            desc: "Make a line plot to display a data set of measurements in fractions of a unit (1/2, 1/4, 1/8). Use operations on fractions for this grade to solve problems involving information presented in line plots. For example, given different measurements of liquid in identical beakers, find the amount of liquid each beaker would contain if the total amount in all the beakers were redistributed equally.",
          },
        ],
      },
      {
        id: "5.MD.C",
        letter: "5C",
        short:
          "Geometric measurement: understand concepts of volume and relate volume to multiplication and to addition.",
        standards: [
          {
            id: "5.MD.C.3",
            s: "C.3",
            desc: "Recognize volume as an attribute of solid figures and understand concepts of volume measurement.",
          },
          {
            id: "5.MD.C.4",
            s: "C.4",
            desc: "Measure volumes by counting unit cubes, using cubic cm, cubic in, cubic ft, and improvised units.",
          },
          {
            id: "5.MD.C.5",
            s: "C.5",
            desc: "Relate volume to the operations of multiplication and addition and solve real world and mathematical problems involving volume.",
          },
        ],
      },
    ],
  },
  {
    id: "5.NBT",
    code: "NBT",
    name: "Number & Operations in Base Ten",
    grade: "5",
    clusters: [
      {
        id: "5.NBT.A",
        letter: "5A",
        short: "Understand the place value system.",
        standards: [
          {
            id: "5.NBT.A.1",
            s: "A.1",
            desc: "Recognize that in a multi-digit number, a digit in one place represents 10 times as much as it represents in the place to its right and 1/10 of what it represents in the place to its left.",
          },
          {
            id: "5.NBT.A.2",
            s: "A.2",
            desc: "Explain patterns in the number of zeros of the product when multiplying a number by powers of 10, and explain patterns in the placement of the decimal point when a decimal is multiplied or divided by a power of 10. Use whole-number exponents to denote powers of 10.",
          },
          {
            id: "5.NBT.A.3",
            s: "A.3",
            desc: "Read, write, and compare decimals to thousandths.",
          },
          {
            id: "5.NBT.A.4",
            s: "A.4",
            desc: "Use place value understanding to round decimals to any place.",
          },
        ],
      },
      {
        id: "5.NBT.B",
        letter: "5B",
        short:
          "Perform operations with multi-digit whole numbers and with decimals to hundredths.",
        standards: [
          {
            id: "5.NBT.B.5",
            s: "B.5",
            desc: "Fluently multiply multi-digit whole numbers using the standard algorithm.",
          },
          {
            id: "5.NBT.B.6",
            s: "B.6",
            desc: "Find whole-number quotients of whole numbers with up to four-digit dividends and two-digit divisors, using strategies based on place value, the properties of operations, and/or the relationship between multiplication and division. Illustrate and explain the calculation by using equations, rectangular arrays, and/or area models.",
          },
          {
            id: "5.NBT.B.7",
            s: "B.7",
            desc: "Add, subtract, multiply, and divide decimals to hundredths, using concrete models or drawings and strategies based on place value, properties of operations, and/or the relationship between addition and subtraction; relate the strategy to a written method and explain the reasoning used.",
          },
        ],
      },
    ],
  },
  {
    id: "5.NF",
    code: "NF",
    name: "Number & Operations - Fractions",
    grade: "5",
    clusters: [
      {
        id: "5.NF.A",
        letter: "5A",
        short:
          "Use equivalent fractions as a strategy to add and subtract fractions.",
        standards: [
          {
            id: "5.NF.A.1",
            s: "A.1",
            desc: "Add and subtract fractions with unlike denominators (including mixed numbers) by replacing given fractions with equivalent fractions in such a way as to produce an equivalent sum or difference of fractions with like denominators. For example, 2/3 + 5/4 = 8/12 + 15/12 = 23/12. (In general, a/b + c/d = (ad + bc)/bd.)",
          },
          {
            id: "5.NF.A.2",
            s: "A.2",
            desc: "Solve word problems involving addition and subtraction of fractions referring to the same whole, including cases of unlike denominators, e.g., by using visual fraction models or equations to represent the problem. Use benchmark fractions and number sense of fractions to estimate mentally and assess the reasonableness of answers. For example, recognize an incorrect result 2/5 + 1/2 = 3/7, by observing that 3/7 < 1/2.",
          },
        ],
      },
      {
        id: "5.NF.B",
        letter: "5B",
        short:
          "Apply and extend previous understandings of multiplication and division to multiply and divide fractions.",
        standards: [
          {
            id: "5.NF.B.3",
            s: "B.3",
            desc: "Interpret a fraction as division of the numerator by the denominator (a/b = a ÷ b). Solve word problems involving division of whole numbers leading to answers in the form of fractions or mixed numbers, e.g., by using visual fraction models or equations to represent the problem. For example, interpret 3/4 as the result of dividing 3 by 4, noting that 3/4 multiplied by 4 equals 3, and that when 3 wholes are shared equally among 4 people each person has a share of size 3/4. If 9 people want to share a 50-pound sack of rice equally by weight, how many pounds of rice should each person get? Between what two whole numbers does your answer lie?",
          },
          {
            id: "5.NF.B.4",
            s: "B.4",
            desc: "Apply and extend previous understandings of multiplication to multiply a fraction or whole number by a fraction.",
          },
          {
            id: "5.NF.B.5",
            s: "B.5",
            desc: "Interpret multiplication as scaling (resizing), by:",
          },
          {
            id: "5.NF.B.6",
            s: "B.6",
            desc: "Solve real world problems involving multiplication of fractions and mixed numbers, e.g., by using visual fraction models or equations to represent the problem.",
          },
          {
            id: "5.NF.B.7",
            s: "B.7",
            desc: "Apply and extend previous understandings of division to divide unit fractions by whole numbers and whole numbers by unit fractions. (Students able to multiply fractions in general can develop strategies to divide fractions in general, by reasoning about the relationship between multiplication and division. But division of a fraction by a fraction is not a requirement at this grade.)",
          },
        ],
      },
    ],
  },
  {
    id: "5.OA",
    code: "OA",
    name: "Operations & Algebraic Thinking",
    grade: "5",
    clusters: [
      {
        id: "5.OA.A",
        letter: "5A",
        short: "Write and interpret numerical expressions.",
        standards: [
          {
            id: "5.OA.A.1",
            s: "A.1",
            desc: "Use parentheses, brackets, or braces in numerical expressions, and evaluate expressions with these symbols.",
          },
          {
            id: "5.OA.A.2",
            s: "A.2",
            desc: "Write simple expressions that record calculations with numbers, and interpret numerical expressions without evaluating them. For example, express the calculation “add 8 and 7, then multiply by 2” as 2 × (8 + 7). Recognize that 3 × (18932 + 921) is three times as large as 18932 + 921, without having to calculate the indicated sum or product.",
          },
        ],
      },
      {
        id: "5.OA.B",
        letter: "5B",
        short: "Analyze patterns and relationships.",
        standards: [
          {
            id: "5.OA.B.3",
            s: "B.3",
            desc: "Generate two numerical patterns using two given rules. Identify apparent relationships between corresponding terms. Form ordered pairs consisting of corresponding terms from the two patterns, and graph the ordered pairs on a coordinate plane. For example, given the rule “Add 3” and the starting number 0, and given the rule “Add 6” and the starting number 0, generate terms in the resulting sequences, and observe that the terms in one sequence are twice the corresponding terms in the other sequence. Explain informally why this is so.",
          },
        ],
      },
    ],
  },
  {
    id: "6.EE",
    code: "EE",
    name: "Expressions & Equations",
    grade: "6",
    clusters: [
      {
        id: "6.EE.A",
        letter: "6A",
        short:
          "Apply and extend previous understandings of arithmetic to algebraic expressions.",
        standards: [
          {
            id: "6.EE.A.1",
            s: "A.1",
            desc: "Write and evaluate numerical expressions involving whole-number exponents.",
          },
          {
            id: "6.EE.A.2",
            s: "A.2",
            desc: "Write, read, and evaluate expressions in which letters stand for numbers.",
          },
          {
            id: "6.EE.A.3",
            s: "A.3",
            desc: "Apply the properties of operations to generate equivalent expressions. For example, apply the distributive property to the expression 3 (2 + x) to produce the equivalent expression 6 + 3x; apply the distributive property to the expression 24x + 18y to produce the equivalent expression 6 (4x + 3y); apply properties of operations to y + y + y to produce the equivalent expression 3y.",
          },
          {
            id: "6.EE.A.4",
            s: "A.4",
            desc: "Identify when two expressions are equivalent (i.e., when the two expressions name the same number regardless of which value is substituted into them). For example, the expressions y + y + y and 3y are equivalent because they name the same number regardless of which number y stands for.",
          },
        ],
      },
      {
        id: "6.EE.B",
        letter: "6B",
        short:
          "Reason about and solve one-variable equations and inequalities.",
        standards: [
          {
            id: "6.EE.B.5",
            s: "B.5",
            desc: "Understand solving an equation or inequality as a process of answering a question: which values from a specified set, if any, make the equation or inequality true? Use substitution to determine whether a given number in a specified set makes an equation or inequality true.",
          },
          {
            id: "6.EE.B.6",
            s: "B.6",
            desc: "Use variables to represent numbers and write expressions when solving a real-world or mathematical problem; understand that a variable can represent an unknown number, or, depending on the purpose at hand, any number in a specified set.",
          },
          {
            id: "6.EE.B.7",
            s: "B.7",
            desc: "Solve real-world and mathematical problems by writing and solving equations of the form x + p = q and px = q for cases in which p, q and x are all nonnegative rational numbers.",
          },
          {
            id: "6.EE.B.8",
            s: "B.8",
            desc: "Write an inequality of the form x > c or x < c to represent a constraint or condition in a real-world or mathematical problem. Recognize that inequalities of the form x > c or x < c have infinitely many solutions; represent solutions of such inequalities on number line diagrams.",
          },
        ],
      },
      {
        id: "6.EE.C",
        letter: "6C",
        short:
          "Represent and analyze quantitative relationships between dependent and independent variables.",
        standards: [
          {
            id: "6.EE.C.9",
            s: "C.9",
            desc: "Use variables to represent two quantities in a real-world problem that change in relationship to one another; write an equation to express one quantity, thought of as the dependent variable, in terms of the other quantity, thought of as the independent variable. Analyze the relationship between the dependent and independent variables using graphs and tables, and relate these to the equation. For example, in a problem involving motion at constant speed, list and graph ordered pairs of distances and times, and write the equation d = 65t to represent the relationship between distance and time.",
          },
        ],
      },
    ],
  },
  {
    id: "6.G",
    code: "G",
    name: "Geometry",
    grade: "6",
    clusters: [
      {
        id: "6.G.A",
        letter: "6A",
        short:
          "Solve real-world and mathematical problems involving area, surface area, and volume.",
        standards: [
          {
            id: "6.G.A.1",
            s: "A.1",
            desc: "Find the area of right triangles, other triangles, special quadrilaterals, and polygons by composing into rectangles or decomposing into triangles and other shapes; apply these techniques in the context of solving real-world and mathematical problems.",
          },
          {
            id: "6.G.A.2",
            s: "A.2",
            desc: "Find the volume of a right rectangular prism with fractional edge lengths by packing it with unit cubes of the appropriate unit fraction edge lengths, and show that the volume is the same as would be found by multiplying the edge lengths of the prism. Apply the formulas V = l w h and V = b h to find volumes of right rectangular prisms with fractional edge lengths in the context of solving real-world and mathematical problems.",
          },
          {
            id: "6.G.A.3",
            s: "A.3",
            desc: "Draw polygons in the coordinate plane given coordinates for the vertices; use coordinates to find the length of a side joining points with the same first coordinate or the same second coordinate. Apply these techniques in the context of solving real-world and mathematical problems.",
          },
          {
            id: "6.G.A.4",
            s: "A.4",
            desc: "Represent three-dimensional figures using nets made up of rectangles and triangles, and use the nets to find the surface area of these figures. Apply these techniques in the context of solving real-world and mathematical problems.",
          },
        ],
      },
    ],
  },
  {
    id: "6.NS",
    code: "NS",
    name: "The Number System",
    grade: "6",
    clusters: [
      {
        id: "6.NS.A",
        letter: "6A",
        short:
          "Apply and extend previous understandings of multiplication and division to divide fractions by fractions.",
        standards: [
          {
            id: "6.NS.A.1",
            s: "A.1",
            desc: "Interpret and compute quotients of fractions, and solve word problems involving division of fractions by fractions, e.g., by using visual fraction models and equations to represent the problem. For example, create a story context for (2/3) ÷ (3/4) and use a visual fraction model to show the quotient; use the relationship between multiplication and division to explain that (2/3) ÷ (3/4) = 8/9 because 3/4 of 8/9 is 2/3. (In general, (a/b) ÷ (c/d) = ad/bc.) How much chocolate will each person get if 3 people share 1/2 lb of chocolate equally? How many 3/4-cup servings are in 2/3 of a cup of yogurt? How wide is a rectangular strip of land with length 3/4 mi and area 1/2 square mi?",
          },
        ],
      },
      {
        id: "6.NS.B",
        letter: "6B",
        short:
          "Compute fluently with multi-digit numbers and find common factors and multiples.",
        standards: [
          {
            id: "6.NS.B.2",
            s: "B.2",
            desc: "Fluently divide multi-digit numbers using the standard algorithm.",
          },
          {
            id: "6.NS.B.3",
            s: "B.3",
            desc: "Fluently add, subtract, multiply, and divide multi-digit decimals using the standard algorithm for each operation.",
          },
          {
            id: "6.NS.B.4",
            s: "B.4",
            desc: "Find the greatest common factor of two whole numbers less than or equal to 100 and the least common multiple of two whole numbers less than or equal to 12. Use the distributive property to express a sum of two whole numbers 1–100 with a common factor as a multiple of a sum of two whole numbers with no common factor. For example, express 36 + 8 as 4 (9 + 2).",
          },
        ],
      },
      {
        id: "6.NS.C",
        letter: "6C",
        short:
          "Apply and extend previous understandings of numbers to the system of rational numbers.",
        standards: [
          {
            id: "6.NS.C.5",
            s: "C.5",
            desc: "Understand that positive and negative numbers are used together to describe quantities having opposite directions or values (e.g., temperature above/below zero, elevation above/below sea level, credits/debits, positive/negative electric charge); use positive and negative numbers to represent quantities in real-world contexts, explaining the meaning of 0 in each situation.",
          },
          {
            id: "6.NS.C.6",
            s: "C.6",
            desc: "Understand a rational number as a point on the number line. Extend number line diagrams and coordinate axes familiar from previous grades to represent points on the line and in the plane with negative number coordinates.",
          },
          {
            id: "6.NS.C.7",
            s: "C.7",
            desc: "Understand ordering and absolute value of rational numbers.",
          },
          {
            id: "6.NS.C.8",
            s: "C.8",
            desc: "Solve real-world and mathematical problems by graphing points in all four quadrants of the coordinate plane. Include use of coordinates and absolute value to find distances between points with the same first coordinate or the same second coordinate.",
          },
        ],
      },
    ],
  },
  {
    id: "6.RP",
    code: "RP",
    name: "Ratios & Proportional Relationships",
    grade: "6",
    clusters: [
      {
        id: "6.RP.A",
        letter: "6A",
        short:
          "Understand ratio concepts and use ratio reasoning to solve problems.",
        standards: [
          {
            id: "6.RP.A.1",
            s: "A.1",
            desc: "Understand the concept of a ratio and use ratio language to describe a ratio relationship between two quantities. For example, “The ratio of wings to beaks in the bird house at the zoo was 2:1, because for every 2 wings there was 1 beak.” “For every vote candidate A received, candidate C received nearly three votes.”",
          },
          {
            id: "6.RP.A.2",
            s: "A.2",
            desc: "Understand the concept of a unit rate a/b associated with a ratio a:b with b ≠0, and use rate language in the context of a ratio relationship. For example, “This recipe has a ratio of 3 cups of flour to 4 cups of sugar, so there is 3/4 cup of flour for each cup of sugar.” “We paid $75 for 15 hamburgers, which is a rate of $5 per hamburger.” (Expectations for unit rates in this grade are limited to non-complex fractions.)",
          },
          {
            id: "6.RP.A.3",
            s: "A.3",
            desc: "Use ratio and rate reasoning to solve real-world and mathematical problems, e.g., by reasoning about tables of equivalent ratios, tape diagrams, double number line diagrams, or equations.",
          },
        ],
      },
    ],
  },
  {
    id: "6.SP",
    code: "SP",
    name: "Statistics & Probability",
    grade: "6",
    clusters: [
      {
        id: "6.SP.A",
        letter: "6A",
        short: "Develop understanding of statistical variability.",
        standards: [
          {
            id: "6.SP.A.1",
            s: "A.1",
            desc: "Recognize a statistical question as one that anticipates variability in the data related to the question and accounts for it in the answers. For example, “How old am I?” is not a statistical question, but “How old are the students in my school?” is a statistical question because one anticipates variability in students’ ages.",
          },
          {
            id: "6.SP.A.2",
            s: "A.2",
            desc: "Understand that a set of data collected to answer a statistical question has a distribution which can be described by its center, spread, and overall shape.",
          },
          {
            id: "6.SP.A.3",
            s: "A.3",
            desc: "Recognize that a measure of center for a numerical data set summarizes all of its values with a single number, while a measure of variation describes how its values vary with a single number.",
          },
        ],
      },
      {
        id: "6.SP.B",
        letter: "6B",
        short: "Summarize and describe distributions.",
        standards: [
          {
            id: "6.SP.B.4",
            s: "B.4",
            desc: "Display numerical data in plots on a number line, including dot plots, histograms, and box plots.",
          },
          {
            id: "6.SP.B.5",
            s: "B.5",
            desc: "Summarize numerical data sets in relation to their context, such as by:",
          },
        ],
      },
    ],
  },
  {
    id: "7.EE",
    code: "EE",
    name: "Expressions & Equations",
    grade: "7",
    clusters: [
      {
        id: "7.EE.A",
        letter: "7A",
        short:
          "Use properties of operations to generate equivalent expressions.",
        standards: [
          {
            id: "7.EE.A.1",
            s: "A.1",
            desc: "Apply properties of operations as strategies to add, subtract, factor, and expand linear expressions with rational coefficients.",
          },
          {
            id: "7.EE.A.2",
            s: "A.2",
            desc: "Understand that rewriting an expression in different forms in a problem context can shed light on the problem and how the quantities in it are related. For example, a + 0.05a = 1.05a means that “increase by 5%” is the same as “multiply by 1.05.”",
          },
        ],
      },
      {
        id: "7.EE.B",
        letter: "7B",
        short:
          "Solve real-life and mathematical problems using numerical and algebraic expressions and equations.",
        standards: [
          {
            id: "7.EE.B.3",
            s: "B.3",
            desc: "Solve multi-step real-life and mathematical problems posed with positive and negative rational numbers in any form (whole numbers, fractions, and decimals), using tools strategically. Apply properties of operations to calculate with numbers in any form; convert between forms as appropriate; and assess the reasonableness of answers using mental computation and estimation strategies. For example: If a woman making $25 an hour gets a 10% raise, she will make an additional 1/10 of her salary an hour, or $2.50, for a new salary of $27.50. If you want to place a towel bar 9 3/4 inches long in the center of a door that is 27 1/2 inches wide, you will need to place the bar about 9 inches from each edge; this estimate can be used as a check on the exact computation.",
          },
          {
            id: "7.EE.B.4",
            s: "B.4",
            desc: "Use variables to represent quantities in a real-world or mathematical problem, and construct simple equations and inequalities to solve problems by reasoning about the quantities.",
          },
        ],
      },
    ],
  },
  {
    id: "7.G",
    code: "G",
    name: "Geometry",
    grade: "7",
    clusters: [
      {
        id: "7.G.A",
        letter: "7A",
        short:
          "Draw, construct, and describe geometrical figures and describe the relationships between them.",
        standards: [
          {
            id: "7.G.A.1",
            s: "A.1",
            desc: "Solve problems involving scale drawings of geometric figures, including computing actual lengths and areas from a scale drawing and reproducing a scale drawing at a different scale.",
          },
          {
            id: "7.G.A.2",
            s: "A.2",
            desc: "Draw (freehand, with ruler and protractor, and with technology) geometric shapes with given conditions. Focus on constructing triangles from three measures of angles or sides, noticing when the conditions determine a unique triangle, more than one triangle, or no triangle.",
          },
          {
            id: "7.G.A.3",
            s: "A.3",
            desc: "Describe the two-dimensional figures that result from slicing three-dimensional figures, as in plane sections of right rectangular prisms and right rectangular pyramids.",
          },
        ],
      },
      {
        id: "7.G.B",
        letter: "7B",
        short:
          "Solve real-life and mathematical problems involving angle measure, area, surface area, and volume.",
        standards: [
          {
            id: "7.G.B.4",
            s: "B.4",
            desc: "Know the formulas for the area and circumference of a circle and use them to solve problems; give an informal derivation of the relationship between the circumference and area of a circle.",
          },
          {
            id: "7.G.B.5",
            s: "B.5",
            desc: "Use facts about supplementary, complementary, vertical, and adjacent angles in a multi-step problem to write and solve simple equations for an unknown angle in a figure.",
          },
          {
            id: "7.G.B.6",
            s: "B.6",
            desc: "Solve real-world and mathematical problems involving area, volume and surface area of two- and three-dimensional objects composed of triangles, quadrilaterals, polygons, cubes, and right prisms.",
          },
        ],
      },
    ],
  },
  {
    id: "7.NS",
    code: "NS",
    name: "The Number System",
    grade: "7",
    clusters: [
      {
        id: "7.NS.A",
        letter: "7A",
        short:
          "Apply and extend previous understandings of operations with fractions to add, subtract, multiply, and divide rational numbers.",
        standards: [
          {
            id: "7.NS.A.1",
            s: "A.1",
            desc: "Apply and extend previous understandings of addition and subtraction to add and subtract rational numbers; represent addition and subtraction on a horizontal or vertical number line diagram.",
          },
          {
            id: "7.NS.A.2",
            s: "A.2",
            desc: "Apply and extend previous understandings of multiplication and division and of fractions to multiply and divide rational numbers.",
          },
          {
            id: "7.NS.A.3",
            s: "A.3",
            desc: "Solve real-world and mathematical problems involving the four operations with rational numbers. (Computations with rational numbers extend the rules for manipulating fractions to complex fractions.)",
          },
        ],
      },
    ],
  },
  {
    id: "7.RP",
    code: "RP",
    name: "Ratios & Proportional Relationships",
    grade: "7",
    clusters: [
      {
        id: "7.RP.A",
        letter: "7A",
        short:
          "Analyze proportional relationships and use them to solve real-world and mathematical problems.",
        standards: [
          {
            id: "7.RP.A.1",
            s: "A.1",
            desc: "Compute unit rates associated with ratios of fractions, including ratios of lengths, areas and other quantities measured in like or different units. For example, if a person walks 1/2 mile in each 1/4 hour, compute the unit rate as the complex fraction 1/2/1/4 miles per hour, equivalently 2 miles per hour.",
          },
          {
            id: "7.RP.A.2",
            s: "A.2",
            desc: "Recognize and represent proportional relationships between quantities.",
          },
          {
            id: "7.RP.A.3",
            s: "A.3",
            desc: "Use proportional relationships to solve multistep ratio and percent problems. Examples: simple interest, tax, markups and markdowns, gratuities and commissions, fees, percent increase and decrease, percent error.",
          },
        ],
      },
    ],
  },
  {
    id: "7.SP",
    code: "SP",
    name: "Statistics & Probability",
    grade: "7",
    clusters: [
      {
        id: "7.SP.A",
        letter: "7A",
        short: "Use random sampling to draw inferences about a population.",
        standards: [
          {
            id: "7.SP.A.1",
            s: "A.1",
            desc: "Understand that statistics can be used to gain information about a population by examining a sample of the population; generalizations about a population from a sample are valid only if the sample is representative of that population. Understand that random sampling tends to produce representative samples and support valid inferences.",
          },
          {
            id: "7.SP.A.2",
            s: "A.2",
            desc: "Use data from a random sample to draw inferences about a population with an unknown characteristic of interest. Generate multiple samples (or simulated samples) of the same size to gauge the variation in estimates or predictions. For example, estimate the mean word length in a book by randomly sampling words from the book; predict the winner of a school election based on randomly sampled survey data. Gauge how far off the estimate or prediction might be.",
          },
        ],
      },
      {
        id: "7.SP.B",
        letter: "7B",
        short: "Draw informal comparative inferences about two populations.",
        standards: [
          {
            id: "7.SP.B.3",
            s: "B.3",
            desc: "Informally assess the degree of visual overlap of two numerical data distributions with similar variabilities, measuring the difference between the centers by expressing it as a multiple of a measure of variability. For example, the mean height of players on the basketball team is 10 cm greater than the mean height of players on the soccer team, about twice the variability (mean absolute deviation) on either team; on a dot plot, the separation between the two distributions of heights is noticeable.",
          },
          {
            id: "7.SP.B.4",
            s: "B.4",
            desc: "Use measures of center and measures of variability for numerical data from random samples to draw informal comparative inferences about two populations. For example, decide whether the words in a chapter of a seventh-grade science book are generally longer than the words in a chapter of a fourth-grade science book.",
          },
        ],
      },
      {
        id: "7.SP.C",
        letter: "7C",
        short:
          "Investigate chance processes and develop, use, and evaluate probability models.",
        standards: [
          {
            id: "7.SP.C.5",
            s: "C.5",
            desc: "Understand that the probability of a chance event is a number between 0 and 1 that expresses the likelihood of the event occurring. Larger numbers indicate greater likelihood. A probability near 0 indicates an unlikely event, a probability around 1/2 indicates an event that is neither unlikely nor likely, and a probability near 1 indicates a likely event.",
          },
          {
            id: "7.SP.C.6",
            s: "C.6",
            desc: "Approximate the probability of a chance event by collecting data on the chance process that produces it and observing its long-run relative frequency, and predict the approximate relative frequency given the probability. For example, when rolling a number cube 600 times, predict that a 3 or 6 would be rolled roughly 200 times, but probably not exactly 200 times.",
          },
          {
            id: "7.SP.C.7",
            s: "C.7",
            desc: "Develop a probability model and use it to find probabilities of events. Compare probabilities from a model to observed frequencies; if the agreement is not good, explain possible sources of the discrepancy.",
          },
          {
            id: "7.SP.C.8",
            s: "C.8",
            desc: "Find probabilities of compound events using organized lists, tables, tree diagrams, and simulation.",
          },
        ],
      },
    ],
  },
  {
    id: "8.EE",
    code: "EE",
    name: "Expressions & Equations",
    grade: "8",
    clusters: [
      {
        id: "8.EE.A",
        letter: "8A",
        short: "Work with radicals and integer exponents.",
        standards: [
          {
            id: "8.EE.A.1",
            s: "A.1",
            desc: "Know and apply the properties of integer exponents to generate equivalent numerical expressions. For example, 3^(2) × 3^(–5) = 3^(–3) = 1/3^(3) = 1/27.",
          },
          {
            id: "8.EE.A.2",
            s: "A.2",
            desc: "Use square root and cube root symbols to represent solutions to equations of the form x^(2) = p and x^(3) = p, where p is a positive rational number. Evaluate square roots of small perfect squares and cube roots of small perfect cubes. Know that Ö2 is irrational.",
          },
          {
            id: "8.EE.A.3",
            s: "A.3",
            desc: "Use numbers expressed in the form of a single digit times an integer power of 10 to estimate very large or very small quantities, and to express how many times as much one is than the other. For example, estimate the population of the United States as 3 × 108 and the population of the world as 7 × 109, and determine that the world population is more than 20 times larger.",
          },
          {
            id: "8.EE.A.4",
            s: "A.4",
            desc: "Perform operations with numbers expressed in scientific notation, including problems where both decimal and scientific notation are used. Use scientific notation and choose units of appropriate size for measurements of very large or very small quantities (e.g., use millimeters per year for seafloor spreading). Interpret scientific notation that has been generated by technology.",
          },
        ],
      },
      {
        id: "8.EE.B",
        letter: "8B",
        short:
          "Understand the connections between proportional relationships, lines, and linear equations.",
        standards: [
          {
            id: "8.EE.B.5",
            s: "B.5",
            desc: "Graph proportional relationships, interpreting the unit rate as the slope of the graph. Compare two different proportional relationships represented in different ways. For example, compare a distance-time graph to a distance-time equation to determine which of two moving objects has greater speed.",
          },
          {
            id: "8.EE.B.6",
            s: "B.6",
            desc: "Use similar triangles to explain why the slope m is the same between any two distinct points on a non-vertical line in the coordinate plane; derive the equation y = mx for a line through the origin and the equation y = mx + b for a line intercepting the vertical axis at b.",
          },
        ],
      },
      {
        id: "8.EE.C",
        letter: "8C",
        short:
          "Analyze and solve linear equations and pairs of simultaneous linear equations.",
        standards: [
          {
            id: "8.EE.C.7",
            s: "C.7",
            desc: "Solve linear equations in one variable.",
          },
          {
            id: "8.EE.C.8",
            s: "C.8",
            desc: "Analyze and solve pairs of simultaneous linear equations.",
          },
        ],
      },
    ],
  },
  {
    id: "8.F",
    code: "F",
    name: "Functions",
    grade: "8",
    clusters: [
      {
        id: "8.F.A",
        letter: "8A",
        short: "Define, evaluate, and compare functions.",
        standards: [
          {
            id: "8.F.A.1",
            s: "A.1",
            desc: "Understand that a function is a rule that assigns to each input exactly one output. The graph of a function is the set of ordered pairs consisting of an input and the corresponding output. (Function notation is not required in Grade 8.)",
          },
          {
            id: "8.F.A.2",
            s: "A.2",
            desc: "Compare properties of two functions each represented in a different way (algebraically, graphically, numerically in tables, or by verbal descriptions). For example, given a linear function represented by a table of values and a linear function represented by an algebraic expression, determine which function has the greater rate of change.",
          },
          {
            id: "8.F.A.3",
            s: "A.3",
            desc: "Interpret the equation y = mx + b as defining a linear function, whose graph is a straight line; give examples of functions that are not linear. For example, the function A = s2 giving the area of a square as a function of its side length is not linear because its graph contains the points (1,1), (2,4) and (3,9), which are not on a straight line.",
          },
        ],
      },
      {
        id: "8.F.B",
        letter: "8B",
        short: "Use functions to model relationships between quantities.",
        standards: [
          {
            id: "8.F.B.4",
            s: "B.4",
            desc: "Construct a function to model a linear relationship between two quantities. Determine the rate of change and initial value of the function from a description of a relationship or from two (x, y) values, including reading these from a table or from a graph. Interpret the rate of change and initial value of a linear function in terms of the situation it models, and in terms of its graph or a table of values.",
          },
          {
            id: "8.F.B.5",
            s: "B.5",
            desc: "Describe qualitatively the functional relationship between two quantities by analyzing a graph (e.g., where the function is increasing or decreasing, linear or nonlinear). Sketch a graph that exhibits the qualitative features of a function that has been described verbally.",
          },
        ],
      },
    ],
  },
  {
    id: "8.G",
    code: "G",
    name: "Geometry",
    grade: "8",
    clusters: [
      {
        id: "8.G.A",
        letter: "8A",
        short:
          "Understand congruence and similarity using physical models, transparencies, or geometry software.",
        standards: [
          {
            id: "8.G.A.1",
            s: "A.1",
            desc: "Verify experimentally the properties of rotations, reflections, and translations:",
          },
          {
            id: "8.G.A.2",
            s: "A.2",
            desc: "Understand that a two-dimensional figure is congruent to another if the second can be obtained from the first by a sequence of rotations, reflections, and translations; given two congruent figures, describe a sequence that exhibits the congruence between them.",
          },
          {
            id: "8.G.A.3",
            s: "A.3",
            desc: "Describe the effect of dilations, translations, rotations, and reflections on two-dimensional figures using coordinates.",
          },
          {
            id: "8.G.A.4",
            s: "A.4",
            desc: "Understand that a two-dimensional figure is similar to another if the second can be obtained from the first by a sequence of rotations, reflections, translations, and dilations; given two similar two-dimensional figures, describe a sequence that exhibits the similarity between them.",
          },
          {
            id: "8.G.A.5",
            s: "A.5",
            desc: "Use informal arguments to establish facts about the angle sum and exterior angle of triangles, about the angles created when parallel lines are cut by a transversal, and the angle-angle criterion for similarity of triangles. For example, arrange three copies of the same triangle so that the sum of the three angles appears to form a line, and give an argument in terms of transversals why this is so.",
          },
        ],
      },
      {
        id: "8.G.B",
        letter: "8B",
        short: "Understand and apply the Pythagorean Theorem.",
        standards: [
          {
            id: "8.G.B.6",
            s: "B.6",
            desc: "Explain a proof of the Pythagorean Theorem and its converse.",
          },
          {
            id: "8.G.B.7",
            s: "B.7",
            desc: "Apply the Pythagorean Theorem to determine unknown side lengths in right triangles in real-world and mathematical problems in two and three dimensions.",
          },
          {
            id: "8.G.B.8",
            s: "B.8",
            desc: "Apply the Pythagorean Theorem to find the distance between two points in a coordinate system.",
          },
        ],
      },
      {
        id: "8.G.C",
        letter: "8C",
        short:
          "Solve real-world and mathematical problems involving volume of cylinders, cones, and spheres.",
        standards: [
          {
            id: "8.G.C.9",
            s: "C.9",
            desc: "Know the formulas for the volumes of cones, cylinders, and spheres and use them to solve real-world and mathematical problems.",
          },
        ],
      },
    ],
  },
  {
    id: "8.NS",
    code: "NS",
    name: "The Number System",
    grade: "8",
    clusters: [
      {
        id: "8.NS.A",
        letter: "8A",
        short:
          "Know that there are numbers that are not rational, and approximate them by rational numbers.",
        standards: [
          {
            id: "8.NS.A.1",
            s: "A.1",
            desc: "Know that numbers that are not rational are called irrational. Understand informally that every number has a decimal expansion; for rational numbers show that the decimal expansion repeats eventually, and convert a decimal expansion which repeats eventually into a rational number.",
          },
          {
            id: "8.NS.A.2",
            s: "A.2",
            desc: "Use rational approximations of irrational numbers to compare the size of irrational numbers, locate them approximately on a number line diagram, and estimate the value of expressions (e.g., p^(2)). For example, by truncating the decimal expansion of Ö2, show that Ö2 is between 1 and 2, then between 1.4 and 1.5, and explain how to continue on to get better approximations.",
          },
        ],
      },
    ],
  },
  {
    id: "8.SP",
    code: "SP",
    name: "Statistics & Probability",
    grade: "8",
    clusters: [
      {
        id: "8.SP.A",
        letter: "8A",
        short: "Investigate patterns of association in bivariate data.",
        standards: [
          {
            id: "8.SP.A.1",
            s: "A.1",
            desc: "Construct and interpret scatter plots for bivariate measurement data to investigate patterns of association between two quantities. Describe patterns such as clustering, outliers, positive or negative association, linear association, and nonlinear association.",
          },
          {
            id: "8.SP.A.2",
            s: "A.2",
            desc: "Know that straight lines are widely used to model relationships between two quantitative variables. For scatter plots that suggest a linear association, informally fit a straight line, and informally assess the model fit by judging the closeness of the data points to the line.",
          },
          {
            id: "8.SP.A.3",
            s: "A.3",
            desc: "Use the equation of a linear model to solve problems in the context of bivariate measurement data, interpreting the slope and intercept. For example, in a linear model for a biology experiment, interpret a slope of 1.5 cm/hr as meaning that an additional hour of sunlight each day is associated with an additional 1.5 cm in mature plant height.",
          },
          {
            id: "8.SP.A.4",
            s: "A.4",
            desc: "Understand that patterns of association can also be seen in bivariate categorical data by displaying frequencies and relative frequencies in a two-way table. Construct and interpret a two-way table summarizing data on two categorical variables collected from the same subjects. Use relative frequencies calculated for rows or columns to describe possible association between the two variables. For example, collect data from students in your class on whether or not they have a curfew on school nights and whether or not they have assigned chores at home. Is there evidence that those who have a curfew also tend to have chores?",
          },
        ],
      },
    ],
  },
  {
    id: "A-APR",
    code: "A-APR",
    name: "Arithmetic with Polynomials & Rational Expressions",
    grade: "HS",
    category: "Algebra",
    clusters: [
      {
        id: "A-APR.A",
        letter: "A",
        short: "Perform arithmetic operations on polynomials.",
        standards: [
          {
            id: "A-APR.A.1",
            s: "A.1",
            desc: "Understand that polynomials form a system analogous to the integers, namely, they are closed under the operations of addition, subtraction, and multiplication; add, subtract, and multiply polynomials.",
          },
        ],
      },
      {
        id: "A-APR.B",
        letter: "B",
        short:
          "Understand the relationship between zeros and factors of polynomials.",
        standards: [
          {
            id: "A-APR.B.2",
            s: "B.2",
            desc: "Know and apply the Remainder Theorem: For a polynomial p(x) and a number a, the remainder on division by x – a is p(a), so p(a) = 0 if and only if (x – a) is a factor of p(x).",
          },
          {
            id: "A-APR.B.3",
            s: "B.3",
            desc: "Identify zeros of polynomials when suitable factorizations are available, and use the zeros to construct a rough graph of the function defined by the polynomial.",
          },
        ],
      },
      {
        id: "A-APR.C",
        letter: "C",
        short: "Use polynomial identities to solve problems.",
        standards: [
          {
            id: "A-APR.C.4",
            s: "C.4",
            desc: "Prove polynomial identities and use them to describe numerical relationships. For example, the polynomial identity (x^(2) + y^(2))^(2) = (x^(2 )– y^(2))^(2) + (2xy)^(2 )can be used to generate Pythagorean triples.",
          },
          {
            id: "A-APR.C.5",
            s: "C.5",
            desc: "Know and apply the Binomial Theorem for the expansion of (x + y)^(n )in powers of x and y for a positive integer n, where x and y are any numbers, with coefficients determined for example by Pascal’s Triangle. (The Binomial Theorem can be proved by mathematical induction or by a combinatorial argument.)",
          },
        ],
      },
      {
        id: "A-APR.D",
        letter: "D",
        short: "Rewrite rational expressions.",
        standards: [
          {
            id: "A-APR.D.6",
            s: "D.6",
            desc: "Rewrite simple rational expressions in different forms; write a(x)/b(x) in the form q(x) + r(x)/b(x), where a(x), b(x), q(x), and r(x) are polynomials with the degree of r(x) less than the degree of b(x), using inspection, long division, or, for the more complicated examples, a computer algebra system.",
          },
          {
            id: "A-APR.D.7",
            s: "D.7",
            desc: "Understand that rational expressions form a system analogous to the rational numbers, closed under addition, subtraction, multiplication, and division by a nonzero rational expression; add, subtract, multiply, and divide rational expressions.",
          },
        ],
      },
    ],
  },
  {
    id: "A-CED",
    code: "A-CED",
    name: "Creating Equations",
    grade: "HS",
    category: "Algebra",
    clusters: [
      {
        id: "A-CED.A",
        letter: "A",
        short: "Create equations that describe numbers or relationships",
        standards: [
          {
            id: "A-CED.A.1",
            s: "A.1",
            desc: "Create equations and inequalities in one variable and use them to solve problems. Include equations arising from linear and quadratic functions, and simple rational and exponential functions.",
          },
          {
            id: "A-CED.A.2",
            s: "A.2",
            desc: "Create equations in two or more variables to represent relationships between quantities; graph equations on coordinate axes with labels and scales.",
          },
          {
            id: "A-CED.A.3",
            s: "A.3",
            desc: "Represent constraints by equations or inequalities, and by systems of equations and/or inequalities, and interpret solutions as viable or nonviable options in a modeling context. For example, represent inequalities describing nutritional and cost constraints on combinations of different foods.",
          },
          {
            id: "A-CED.A.4",
            s: "A.4",
            desc: "Rearrange formulas to highlight a quantity of interest, using the same reasoning as in solving equations. For example, rearrange Ohm’s law V = IR to highlight resistance R.",
          },
        ],
      },
    ],
  },
  {
    id: "A-REI",
    code: "A-REI",
    name: "Reasoning with Equations & Inequalities",
    grade: "HS",
    category: "Algebra",
    clusters: [
      {
        id: "A-REI.A",
        letter: "A",
        short:
          "Understand solving equations as a process of reasoning and explain the reasoning.",
        standards: [
          {
            id: "A-REI.A.1",
            s: "A.1",
            desc: "Explain each step in solving a simple equation as following from the equality of numbers asserted at the previous step, starting from the assumption that the original equation has a solution. Construct a viable argument to justify a solution method.",
          },
          {
            id: "A-REI.A.2",
            s: "A.2",
            desc: "Solve simple rational and radical equations in one variable, and give examples showing how extraneous solutions may arise.",
          },
        ],
      },
      {
        id: "A-REI.B",
        letter: "B",
        short: "Solve equations and inequalities in one variable.",
        standards: [
          {
            id: "A-REI.B.3",
            s: "B.3",
            desc: "Solve linear equations and inequalities in one variable, including equations with coefficients represented by letters.",
          },
          {
            id: "A-REI.B.4",
            s: "B.4",
            desc: "Solve quadratic equations in one variable.",
          },
        ],
      },
      {
        id: "A-REI.C",
        letter: "C",
        short: "Solve systems of equations.",
        standards: [
          {
            id: "A-REI.C.5",
            s: "C.5",
            desc: "Prove that, given a system of two equations in two variables, replacing one equation by the sum of that equation and a multiple of the other produces a system with the same solutions.",
          },
          {
            id: "A-REI.C.6",
            s: "C.6",
            desc: "Solve systems of linear equations exactly and approximately (e.g., with graphs), focusing on pairs of linear equations in two variables.",
          },
          {
            id: "A-REI.C.7",
            s: "C.7",
            desc: "Solve a simple system consisting of a linear equation and a quadratic equation in two variables algebraically and graphically. For example, find the points of intersection between the line y = –3x and the circle x^(2 )+ y^(2) = 3.",
          },
          {
            id: "A-REI.C.8",
            s: "C.8",
            desc: "Represent a system of linear equations as a single matrix equation in a vector variable.",
          },
          {
            id: "A-REI.C.9",
            s: "C.9",
            desc: "Find the inverse of a matrix if it exists and use it to solve systems of linear equations (using technology for matrices of dimension 3 × 3 or greater).",
          },
        ],
      },
      {
        id: "A-REI.D",
        letter: "D",
        short: "Represent and solve equations and inequalities graphically.",
        standards: [
          {
            id: "A-REI.D.10",
            s: "D.10",
            desc: "Understand that the graph of an equation in two variables is the set of all its solutions plotted in the coordinate plane, often forming a curve (which could be a line).",
          },
          {
            id: "A-REI.D.11",
            s: "D.11",
            desc: "Explain why the x-coordinates of the points where the graphs of the equations y = f(x) and y = g(x) intersect are the solutions of the equation f(x) = g(x); find the solutions approximately, e.g., using technology to graph the functions, make tables of values, or find successive approximations. Include cases where f(x) and/or g(x) are linear, polynomial, rational, absolute value, exponential, and logarithmic functions.",
          },
          {
            id: "A-REI.D.12",
            s: "D.12",
            desc: "Graph the solutions to a linear inequality in two variables as a half plane (excluding the boundary in the case of a strict inequality), and graph the solution set to a system of linear inequalities in two variables as the intersection of the corresponding half-planes.",
          },
        ],
      },
    ],
  },
  {
    id: "A-SSE",
    code: "A-SSE",
    name: "Seeing Structure in Expressions",
    grade: "HS",
    category: "Algebra",
    clusters: [
      {
        id: "A-SSE.A",
        letter: "A",
        short: "Interpret the structure of expressions.",
        standards: [
          {
            id: "A-SSE.A.1",
            s: "A.1",
            desc: "Interpret expressions that represent a quantity in terms of its context.",
          },
          {
            id: "A-SSE.A.2",
            s: "A.2",
            desc: "Use the structure of an expression to identify ways to rewrite it. For example, see x^(4) – y^(4 )as (x^(2))^(2 )– (y^(2))^(2), thus recognizing it as a difference of squares that can be factored as (x^(2 )– y^(2))(x^(2 )+ y^(2)).",
          },
        ],
      },
      {
        id: "A-SSE.B",
        letter: "B",
        short: "Write expressions in equivalent forms to solve problems.",
        standards: [
          {
            id: "A-SSE.B.3",
            s: "B.3",
            desc: "Choose and produce an equivalent form of an expression to reveal and explain properties of the quantity represented by the expression.",
          },
          {
            id: "A-SSE.B.4",
            s: "B.4",
            desc: "Derive the formula for the sum of a finite geometric series (when the common ratio is not 1), and use the formula to solve problems. For example, calculate mortgage payments.",
          },
        ],
      },
    ],
  },
  {
    id: "F-BF",
    code: "F-BF",
    name: "Building Functions",
    grade: "HS",
    category: "Functions",
    clusters: [
      {
        id: "F-BF.A",
        letter: "A",
        short:
          "Build a function that models a relationship between two quantities.",
        standards: [
          {
            id: "F-BF.A.1",
            s: "A.1",
            desc: "Write a function that describes a relationship between two quantities.",
          },
          {
            id: "F-BF.A.2",
            s: "A.2",
            desc: "Write arithmetic and geometric sequences both recursively and with an explicit formula, use them to model situations, and translate between the two forms.",
          },
        ],
      },
      {
        id: "F-BF.B",
        letter: "B",
        short: "Build new functions from existing functions.",
        standards: [
          {
            id: "F-BF.B.3",
            s: "B.3",
            desc: "Identify the effect on the graph of replacing f(x) by f(x) + k, k f(x), f(kx), and f(x + k) for specific values of k (both positive and negative); find the value of k given the graphs. Experiment with cases and illustrate an explanation of the effects on the graph using technology. Include recognizing even and odd functions from their graphs and algebraic expressions for them.",
          },
          {
            id: "F-BF.B.4",
            s: "B.4",
            desc: "Find inverse functions.",
          },
          {
            id: "F-BF.B.5",
            s: "B.5",
            desc: "Understand the inverse relationship between exponents and logarithms and use this relationship to solve problems involving logarithms and exponents.",
          },
        ],
      },
    ],
  },
  {
    id: "F-IF",
    code: "F-IF",
    name: "Interpreting Functions",
    grade: "HS",
    category: "Functions",
    clusters: [
      {
        id: "F-IF.A",
        letter: "A",
        short:
          "Understand the concept of a function and use function notation.",
        standards: [
          {
            id: "F-IF.A.1",
            s: "A.1",
            desc: "Understand that a function from one set (called the domain) to another set (called the range) assigns to each element of the domain exactly one element of the range. If f is a function and x is an element of its domain, then f(x) denotes the output of f corresponding to the input x. The graph of f is the graph of the equation y = f(x).",
          },
          {
            id: "F-IF.A.2",
            s: "A.2",
            desc: "Use function notation, evaluate functions for inputs in their domains, and interpret statements that use function notation in terms of a context.",
          },
          {
            id: "F-IF.A.3",
            s: "A.3",
            desc: "Recognize that sequences are functions, sometimes defined recursively, whose domain is a subset of the integers. For example, the Fibonacci sequence is defined recursively by f(0) = f(1) = 1, f(n+1) = f(n) + f(n-1) for n ³ 1.",
          },
        ],
      },
      {
        id: "F-IF.B",
        letter: "B",
        short:
          "Interpret functions that arise in applications in terms of the context.",
        standards: [
          {
            id: "F-IF.B.4",
            s: "B.4",
            desc: "For a function that models a relationship between two quantities, interpret key features of graphs and tables in terms of the quantities, and sketch graphs showing key features given a verbal description of the relationship. Key features include: intercepts; intervals where the function is increasing, decreasing, positive, or negative; relative maximums and minimums; symmetries; end behavior; and periodicity.",
          },
          {
            id: "F-IF.B.5",
            s: "B.5",
            desc: "Relate the domain of a function to its graph and, where applicable, to the quantitative relationship it describes.  For example, if the function h(n) gives the number of person-hours it takes to assemble n engines in a factory, then the positive integers would be an appropriate domain for the function.",
          },
          {
            id: "F-IF.B.6",
            s: "B.6",
            desc: "Calculate and interpret the average rate of change of a function (presented symbolically or as a table) over a specified interval. Estimate the rate of change from a graph.",
          },
        ],
      },
      {
        id: "F-IF.C",
        letter: "C",
        short: "Analyze functions using different representations.",
        standards: [
          {
            id: "F-IF.C.7",
            s: "C.7",
            desc: "Graph functions expressed symbolically and show key features of the graph, by hand in simple cases and using technology for more complicated cases.",
          },
          {
            id: "F-IF.C.8",
            s: "C.8",
            desc: "Write a function defined by an expression in different but equivalent forms to reveal and explain different properties of the function.",
          },
          {
            id: "F-IF.C.9",
            s: "C.9",
            desc: "Compare properties of two functions each represented in a different way (algebraically, graphically, numerically in tables, or by verbal descriptions). For example, given a graph of one quadratic function and an algebraic expression for another, say which has the larger maximum.",
          },
        ],
      },
    ],
  },
  {
    id: "F-LE",
    code: "F-LE",
    name: "Linear, Quadratic, & Exponential Models",
    grade: "HS",
    category: "Functions",
    clusters: [
      {
        id: "F-LE.A",
        letter: "A",
        short:
          "Construct and compare linear and exponential models and solve problems.",
        standards: [
          {
            id: "F-LE.A.1",
            s: "A.1",
            desc: "Distinguish between situations that can be modeled with linear functions and with exponential functions.",
          },
          {
            id: "F-LE.A.2",
            s: "A.2",
            desc: "Construct linear and exponential functions, including arithmetic and geometric sequences, given a graph, a description of a relationship, or two input-output pairs (include reading these from a table).",
          },
          {
            id: "F-LE.A.3",
            s: "A.3",
            desc: "Observe using graphs and tables that a quantity increasing exponentially eventually exceeds a quantity increasing linearly, quadratically, or (more generally) as a polynomial function.",
          },
          {
            id: "F-LE.A.4",
            s: "A.4",
            desc: "For exponential models, express as a logarithm the solution to ab^(ct) = d where a, c, and d are numbers and the base b is 2, 10, or e; evaluate the logarithm using technology.",
          },
        ],
      },
      {
        id: "F-LE.B",
        letter: "B",
        short:
          "Interpret expressions for functions in terms of the situation they model.",
        standards: [
          {
            id: "F-LE.B.5",
            s: "B.5",
            desc: "Interpret the parameters in a linear or exponential function in terms of a context.",
          },
        ],
      },
    ],
  },
  {
    id: "F-TF",
    code: "F-TF",
    name: "Trigonometric Functions",
    grade: "HS",
    category: "Functions",
    clusters: [
      {
        id: "F-TF.A",
        letter: "A",
        short:
          "Extend the domain of trigonometric functions using the unit circle.",
        standards: [
          {
            id: "F-TF.A.1",
            s: "A.1",
            desc: "Understand radian measure of an angle as the length of the arc on the unit circle subtended by the angle.",
          },
          {
            id: "F-TF.A.2",
            s: "A.2",
            desc: "Explain how the unit circle in the coordinate plane enables the extension of trigonometric functions to all real numbers, interpreted as radian measures of angles traversed counterclockwise around the unit circle.",
          },
          {
            id: "F-TF.A.3",
            s: "A.3",
            desc: "Use special triangles to determine geometrically the values of sine, cosine, tangent for π/3, π/4 and π/6, and use the unit circle to express the values of sine, cosines, and tangent for π–x, π+x, and 2π–x in terms of their values for x, where x is any real number.",
          },
          {
            id: "F-TF.A.4",
            s: "A.4",
            desc: "Use the unit circle to explain symmetry (odd and even) and periodicity of trigonometric functions.",
          },
        ],
      },
      {
        id: "F-TF.B",
        letter: "B",
        short: "Model periodic phenomena with trigonometric functions.",
        standards: [
          {
            id: "F-TF.B.5",
            s: "B.5",
            desc: "Choose trigonometric functions to model periodic phenomena with specified amplitude, frequency, and midline.",
          },
          {
            id: "F-TF.B.6",
            s: "B.6",
            desc: "Understand that restricting a trigonometric function to a domain on which it is always increasing or always decreasing allows its inverse to be constructed.",
          },
          {
            id: "F-TF.B.7",
            s: "B.7",
            desc: "Use inverse functions to solve trigonometric equations that arise in modeling contexts; evaluate the solutions using technology, and interpret them in terms of the context.",
          },
        ],
      },
      {
        id: "F-TF.C",
        letter: "C",
        short: "Prove and apply trigonometric identities.",
        standards: [
          {
            id: "F-TF.C.8",
            s: "C.8",
            desc: "Prove the Pythagorean identity sin^(2)(θ) + cos^(2)(θ) = 1 and use it to find sin(θ), cos(θ),or tan(θ) given sin(θ), cos(θ), or tan(θ) and the quadrant of the angle.",
          },
          {
            id: "F-TF.C.9",
            s: "C.9",
            desc: "Prove the addition and subtraction formulas for sine, cosine, and tangent and use them to solve problems.",
          },
        ],
      },
    ],
  },
  {
    id: "G-C",
    code: "G-C",
    name: "Circles",
    grade: "HS",
    category: "Geometry",
    clusters: [
      {
        id: "G-C.A",
        letter: "A",
        short: "Understand and apply theorems about circles.",
        standards: [
          {
            id: "G-C.A.1",
            s: "A.1",
            desc: "Prove that all circles are similar.",
          },
          {
            id: "G-C.A.2",
            s: "A.2",
            desc: "Identify and describe relationships among inscribed angles, radii, and chords. Include the relationship between central, inscribed, and circumscribed angles; inscribed angles on a diameter are right angles; the radius of a circle is perpendicular to the tangent where the radius intersects the circle.",
          },
          {
            id: "G-C.A.3",
            s: "A.3",
            desc: "Construct the inscribed and circumscribed circles of a triangle, and prove properties of angles for a quadrilateral inscribed in a circle.",
          },
          {
            id: "G-C.A.4",
            s: "A.4",
            desc: "Construct a tangent line from a point outside a given circle to the circle.",
          },
        ],
      },
      {
        id: "G-C.B",
        letter: "B",
        short: "Find arc lengths and areas of sectors of circles.",
        standards: [
          {
            id: "G-C.B.5",
            s: "B.5",
            desc: "Derive using similarity the fact that the length of the arc intercepted by an angle is proportional to the radius, and define the radian measure of the angle as the constant of proportionality; derive the formula for the area of a sector.",
          },
        ],
      },
    ],
  },
  {
    id: "G-CO",
    code: "G-CO",
    name: "Congruence",
    grade: "HS",
    category: "Geometry",
    clusters: [
      {
        id: "G-CO.A",
        letter: "A",
        short: "Experiment with transformations in the plane.",
        standards: [
          {
            id: "G-CO.A.1",
            s: "A.1",
            desc: "Know precise definitions of angle, circle, perpendicular line, parallel line, and line segment, based on the undefined notions of point, line, distance along a line, and distance around a circular arc.",
          },
          {
            id: "G-CO.A.2",
            s: "A.2",
            desc: "Represent transformations in the plane using, e.g., transparencies and geometry software; describe transformations as functions that take points in the plane as inputs and give other points as outputs. Compare transformations that preserve distance and angle to those that do not (e.g., translation versus horizontal stretch).",
          },
          {
            id: "G-CO.A.3",
            s: "A.3",
            desc: "Given a rectangle, parallelogram, trapezoid, or regular polygon, describe the rotations and reflections that carry it onto itself.",
          },
          {
            id: "G-CO.A.4",
            s: "A.4",
            desc: "Develop definitions of rotations, reflections, and translations in terms of angles, circles, perpendicular lines, parallel lines, and line segments.",
          },
          {
            id: "G-CO.A.5",
            s: "A.5",
            desc: "Given a geometric figure and a rotation, reflection, or translation, draw the transformed figure using, e.g., graph paper, tracing paper, or geometry software. Specify a sequence of transformations that will carry a given figure onto another.",
          },
        ],
      },
      {
        id: "G-CO.B",
        letter: "B",
        short: "Understand congruence in terms of rigid motions.",
        standards: [
          {
            id: "G-CO.B.6",
            s: "B.6",
            desc: "Use geometric descriptions of rigid motions to transform figures and to predict the effect of a given rigid motion on a given figure; given two figures, use the definition of congruence in terms of rigid motions to decide if they are congruent.",
          },
          {
            id: "G-CO.B.7",
            s: "B.7",
            desc: "Use the definition of congruence in terms of rigid motions to show that two triangles are congruent if and only if corresponding pairs of sides and corresponding pairs of angles are congruent.",
          },
          {
            id: "G-CO.B.8",
            s: "B.8",
            desc: "Explain how the criteria for triangle congruence (ASA, SAS, and SSS) follow from the definition of congruence in terms of rigid motions.",
          },
        ],
      },
      {
        id: "G-CO.C",
        letter: "C",
        short: "Prove geometric theorems.",
        standards: [
          {
            id: "G-CO.C.10",
            s: "C.10",
            desc: "Prove theorems about triangles. Theorems include: measures of interior angles of a triangle sum to 180°; base angles of isosceles triangles are congruent; the segment joining midpoints of two sides of a triangle is parallel to the third side and half the length; the medians of a triangle meet at a point.",
          },
          {
            id: "G-CO.C.11",
            s: "C.11",
            desc: "Prove theorems about parallelograms. Theorems include: opposite sides are congruent, opposite angles are congruent, the diagonals of a parallelogram bisect each other, and conversely, rectangles are parallelograms with congruent diagonals.",
          },
          {
            id: "G-CO.C.9",
            s: "C.9",
            desc: "Prove theorems about lines and angles. Theorems include: vertical angles are congruent; when a transversal crosses parallel lines, alternate interior angles are congruent and corresponding angles are congruent; points on a perpendicular bisector of a line segment are exactly those equidistant from the segment’s endpoints.",
          },
        ],
      },
      {
        id: "G-CO.D",
        letter: "D",
        short: "Make geometric constructions.",
        standards: [
          {
            id: "G-CO.D.12",
            s: "D.12",
            desc: "Make formal geometric constructions with a variety of tools and methods (compass and straightedge, string, reflective devices, paper folding, dynamic geometric software, etc.). Copying a segment; copying an angle; bisecting a segment; bisecting an angle; constructing perpendicular lines, including the perpendicular bisector of a line segment; and constructing a line parallel to a given line through a point not on the line.",
          },
          {
            id: "G-CO.D.13",
            s: "D.13",
            desc: "Construct an equilateral triangle, a square, and a regular hexagon inscribed in a circle.",
          },
        ],
      },
    ],
  },
  {
    id: "G-GMD",
    code: "G-GMD",
    name: "Geometric Measurement & Dimension",
    grade: "HS",
    category: "Geometry",
    clusters: [
      {
        id: "G-GMD.A",
        letter: "A",
        short: "Explain volume formulas and use them to solve problems.",
        standards: [
          {
            id: "G-GMD.A.1",
            s: "A.1",
            desc: "Give an informal argument for the formulas for the circumference of a circle, area of a circle, volume of a cylinder, pyramid, and cone. Use dissection arguments, Cavalieri’s principle, and informal limit arguments.",
          },
          {
            id: "G-GMD.A.2",
            s: "A.2",
            desc: "Give an informal argument using Cavalieri’s principle for the formulas for the volume of a sphere and other solid figures.",
          },
          {
            id: "G-GMD.A.3",
            s: "A.3",
            desc: "Use volume formulas for cylinders, pyramids, cones, and spheres to solve problems.",
          },
        ],
      },
      {
        id: "G-GMD.B",
        letter: "B",
        short:
          "Visualize relationships between two-dimensional and three-dimensional objects.",
        standards: [
          {
            id: "G-GMD.B.4",
            s: "B.4",
            desc: "Identify the shapes of two-dimensional cross-sections of three-dimensional objects, and identify three-dimensional objects generated by rotations of two-dimensional objects.",
          },
        ],
      },
    ],
  },
  {
    id: "G-GPE",
    code: "G-GPE",
    name: "Expressing Geometric Properties with Equations",
    grade: "HS",
    category: "Geometry",
    clusters: [
      {
        id: "G-GPE.A",
        letter: "A",
        short:
          "Translate between the geometric description and the equation for a conic section.",
        standards: [
          {
            id: "G-GPE.A.1",
            s: "A.1",
            desc: "Derive the equation of a circle of given center and radius using the Pythagorean Theorem; complete the square to find the center and radius of a circle given by an equation.",
          },
          {
            id: "G-GPE.A.2",
            s: "A.2",
            desc: "Derive the equation of a parabola given a focus and directrix.",
          },
          {
            id: "G-GPE.A.3",
            s: "A.3",
            desc: "(+)Derive the equations of ellipses and hyperbolas given the foci, using the fact that the sum or difference of distances from the foci is constant.",
          },
        ],
      },
      {
        id: "G-GPE.B",
        letter: "B",
        short:
          "Use coordinates to prove simple geometric theorems algebraically.",
        standards: [
          {
            id: "G-GPE.B.4",
            s: "B.4",
            desc: "Use coordinates to prove simple geometric theorems algebraically. For example, prove or disprove that a figure defined by four given points in the coordinate plane is a rectangle; prove or disprove that the point (1, Ö3) lies on the circle centered at the origin and containing the point (0, 2).",
          },
          {
            id: "G-GPE.B.5",
            s: "B.5",
            desc: "Prove the slope criteria for parallel and perpendicular lines and use them to solve geometric problems (e.g., find the equation of a line parallel or perpendicular to a given line that passes through a given point).",
          },
          {
            id: "G-GPE.B.6",
            s: "B.6",
            desc: "Find the point on a directed line segment between two given points that partitions the segment in a given ratio.",
          },
          {
            id: "G-GPE.B.7",
            s: "B.7",
            desc: "Use coordinates to compute perimeters of polygons and areas of triangles and rectangles, e.g., using the distance formula.",
          },
        ],
      },
    ],
  },
  {
    id: "G-MG",
    code: "G-MG",
    name: "Modeling with Geometry",
    grade: "HS",
    category: "Geometry",
    clusters: [
      {
        id: "G-MG.A",
        letter: "A",
        short: "Apply geometric concepts in modeling situations.",
        standards: [
          {
            id: "G-MG.A.1",
            s: "A.1",
            desc: "Use geometric shapes, their measures, and their properties to describe objects (e.g., modeling a tree trunk or a human torso as a cylinder).",
          },
          {
            id: "G-MG.A.2",
            s: "A.2",
            desc: "Apply concepts of density based on area and volume in modeling situations (e.g., persons per square mile, BTUs per cubic foot).",
          },
          {
            id: "G-MG.A.3",
            s: "A.3",
            desc: "Apply geometric methods to solve design problems (e.g., designing an object or structure to satisfy physical constraints or minimize cost; working with typographic grid systems based on ratios).",
          },
        ],
      },
    ],
  },
  {
    id: "G-SRT",
    code: "G-SRT",
    name: "Similarity, Right Triangles, & Trigonometry",
    grade: "HS",
    category: "Geometry",
    clusters: [
      {
        id: "G-SRT.A",
        letter: "A",
        short: "Understand similarity in terms of similarity transformations.",
        standards: [
          {
            id: "G-SRT.A.1",
            s: "A.1",
            desc: "Verify experimentally the properties of dilations given by a center and a scale factor:",
          },
          {
            id: "G-SRT.A.2",
            s: "A.2",
            desc: "Given two figures, use the definition of similarity in terms of similarity transformations to decide if they are similar; explain using similarity transformations the meaning of similarity for triangles as the equality of all corresponding pairs of angles and the proportionality of all corresponding pairs of sides.",
          },
          {
            id: "G-SRT.A.3",
            s: "A.3",
            desc: "Use the properties of similarity transformations to establish the AA criterion for two triangles to be similar.",
          },
        ],
      },
      {
        id: "G-SRT.B",
        letter: "B",
        short: "Prove theorems involving similarity.",
        standards: [
          {
            id: "G-SRT.B.4",
            s: "B.4",
            desc: "Prove theorems about triangles. Theorems include: a line parallel to one side of a triangle divides the other two proportionally, and conversely; the Pythagorean Theorem proved using triangle similarity.",
          },
          {
            id: "G-SRT.B.5",
            s: "B.5",
            desc: "Use congruence and similarity criteria for triangles to solve problems and to prove relationships in geometric figures.",
          },
        ],
      },
      {
        id: "G-SRT.C",
        letter: "C",
        short:
          "Define trigonometric ratios and solve problems involving right triangles.",
        standards: [
          {
            id: "G-SRT.C.6",
            s: "C.6",
            desc: "Understand that by similarity, side ratios in right triangles are properties of the angles in the triangle, leading to definitions of trigonometric ratios for acute angles.",
          },
          {
            id: "G-SRT.C.7",
            s: "C.7",
            desc: "Explain and use the relationship between the sine and cosine of complementary angles.",
          },
          {
            id: "G-SRT.C.8",
            s: "C.8",
            desc: "Use trigonometric ratios and the Pythagorean Theorem to solve right triangles in applied problems.",
          },
        ],
      },
      {
        id: "G-SRT.D",
        letter: "D",
        short: "Apply trigonometry to general triangles.",
        standards: [
          {
            id: "G-SRT.D.10",
            s: "D.10",
            desc: "Prove the Laws of Sines and Cosines and use them to solve problems.",
          },
          {
            id: "G-SRT.D.11",
            s: "D.11",
            desc: "Understand and apply the Law of Sines and the Law of Cosines to find unknown measurements in right and non-right triangles (e.g., surveying problems, resultant forces).",
          },
          {
            id: "G-SRT.D.9",
            s: "D.9",
            desc: "Derive the formula A = 1/2 ab sin(C) for the area of a triangle by drawing an auxiliary line from a vertex perpendicular to the opposite side.",
          },
        ],
      },
    ],
  },
  {
    id: "N-CN",
    code: "N-CN",
    name: "The Complex Number System",
    grade: "HS",
    category: "Number and Quantity",
    clusters: [
      {
        id: "N-CN.A",
        letter: "A",
        short: "Perform arithmetic operations with complex numbers.",
        standards: [
          {
            id: "N-CN.A.1",
            s: "A.1",
            desc: "Know there is a complex number i such that i^(2) = –1, and every complex number has the form a + bi with a and b real.",
          },
          {
            id: "N-CN.A.2",
            s: "A.2",
            desc: "Use the relation i^(2) = –1 and the commutative, associative, and distributive properties to add, subtract, and multiply complex numbers.",
          },
          {
            id: "N-CN.A.3",
            s: "A.3",
            desc: "Find the conjugate of a complex number; use conjugates to find moduli and quotients of complex numbers.",
          },
        ],
      },
      {
        id: "N-CN.B",
        letter: "B",
        short:
          "Represent complex numbers and their operations on the complex plane.",
        standards: [
          {
            id: "N-CN.B.4",
            s: "B.4",
            desc: "Represent complex numbers on the complex plane in rectangular and polar form (including real and imaginary numbers), and explain why the rectangular and polar forms of a given complex number represent the same number.",
          },
          {
            id: "N-CN.B.5",
            s: "B.5",
            desc: "Represent addition, subtraction, multiplication, and conjugation of complex numbers geometrically on the complex plane; use properties of this representation for computation. For example, (-1 + Ö3i)^(3) = 8 because (-1 + Ö3i) has modulus 2 and argument 120°.",
          },
          {
            id: "N-CN.B.6",
            s: "B.6",
            desc: "Calculate the distance between numbers in the complex plane as the modulus of the difference, and the midpoint of a segment as the average of the numbers at its endpoints.",
          },
        ],
      },
      {
        id: "N-CN.C",
        letter: "C",
        short: "Use complex numbers in polynomial identities and equations.",
        standards: [
          {
            id: "N-CN.C.7",
            s: "C.7",
            desc: "Solve quadratic equations with real coefficients that have complex solutions.",
          },
          {
            id: "N-CN.C.8",
            s: "C.8",
            desc: "Extend polynomial identities to the complex numbers. For example, rewrite x^(2) + 4 as (x + 2i)(x – 2i).",
          },
          {
            id: "N-CN.C.9",
            s: "C.9",
            desc: "Know the Fundamental Theorem of Algebra; show that it is true for quadratic polynomials.",
          },
        ],
      },
    ],
  },
  {
    id: "N-Q",
    code: "N-Q",
    name: "Quantities",
    grade: "HS",
    category: "Number and Quantity",
    clusters: [
      {
        id: "N-Q.A",
        letter: "A",
        short: "Reason quantitatively and use units to solve problems.",
        standards: [
          {
            id: "N-Q.A.1",
            s: "A.1",
            desc: "Use units as a way to understand problems and to guide the solution of multi-step problems; choose and interpret units consistently in formulas; choose and interpret the scale and the origin in graphs and data displays.",
          },
          {
            id: "N-Q.A.2",
            s: "A.2",
            desc: "Define appropriate quantities for the purpose of descriptive modeling.",
          },
          {
            id: "N-Q.A.3",
            s: "A.3",
            desc: "Choose a level of accuracy appropriate to limitations on measurement when reporting quantities.",
          },
        ],
      },
    ],
  },
  {
    id: "N-RN",
    code: "N-RN",
    name: "The Real Number System",
    grade: "HS",
    category: "Number and Quantity",
    clusters: [
      {
        id: "N-RN.A",
        letter: "A",
        short: "Extend the properties of exponents to rational exponents.",
        standards: [
          {
            id: "N-RN.A.1",
            s: "A.1",
            desc: "Explain how the definition of the meaning of rational exponents follows from extending the properties of integer exponents to those values, allowing for a notation for radicals in terms of rational exponents. For example, we define 5^(1/3) to be the cube root of 5 because we want (5^(1/3))^(3) = 5(^(1/3))^(3) to hold, so (5^(1/3))^(3) must equal 5.",
          },
          {
            id: "N-RN.A.2",
            s: "A.2",
            desc: "Rewrite expressions involving radicals and rational exponents using the properties of exponents.",
          },
        ],
      },
      {
        id: "N-RN.B",
        letter: "B",
        short: "Use properties of rational and irrational numbers.",
        standards: [
          {
            id: "N-RN.B.3",
            s: "B.3",
            desc: "Explain why the sum or product of two rational numbers is rational; that the sum of a rational number and an irrational number is irrational; and that the product of a nonzero rational number and an irrational number is irrational.",
          },
        ],
      },
    ],
  },
  {
    id: "N-VM",
    code: "N-VM",
    name: "Vector & Matrix Quantities",
    grade: "HS",
    category: "Number and Quantity",
    clusters: [
      {
        id: "N-VM.A",
        letter: "A",
        short: "Represent and model with vector quantities.",
        standards: [
          {
            id: "N-VM.A.1",
            s: "A.1",
            desc: "Recognize vector quantities as having both magnitude and direction. Represent vector quantities by directed line segments, and use appropriate symbols for vectors and their magnitudes (e.g., v, |v|, ||v||, v).",
          },
          {
            id: "N-VM.A.2",
            s: "A.2",
            desc: "Find the components of a vector by subtracting the coordinates of an initial point from the coordinates of a terminal point.",
          },
          {
            id: "N-VM.A.3",
            s: "A.3",
            desc: "Solve problems involving velocity and other quantities that can be represented by vectors.",
          },
        ],
      },
      {
        id: "N-VM.B",
        letter: "B",
        short: "Perform operations on vectors.",
        standards: [
          {
            id: "N-VM.B.4",
            s: "B.4",
            desc: "Add and subtract vectors.",
          },
          {
            id: "N-VM.B.5",
            s: "B.5",
            desc: "Multiply a vector by a scalar.",
          },
        ],
      },
      {
        id: "N-VM.C",
        letter: "C",
        short:
          "Perform operations on matrices and use matrices in applications.",
        standards: [
          {
            id: "N-VM.C.10",
            s: "C.10",
            desc: "Understand that the zero and identity matrices play a role in matrix addition and multiplication similar to the role of 0 and 1 in the real numbers. The determinant of a square matrix is nonzero if and only if the matrix has a multiplicative inverse.",
          },
          {
            id: "N-VM.C.11",
            s: "C.11",
            desc: "Multiply a vector (regarded as a matrix with one column) by a matrix of suitable dimensions to produce another vector. Work with matrices as transformations of vectors.",
          },
          {
            id: "N-VM.C.12",
            s: "C.12",
            desc: "Work with 2 × 2 matrices as a transformations of the plane, and interpret the absolute value of the determinant in terms of area.",
          },
          {
            id: "N-VM.C.6",
            s: "C.6",
            desc: "Use matrices to represent and manipulate data, e.g., to represent payoffs or incidence relationships in a network.",
          },
          {
            id: "N-VM.C.7",
            s: "C.7",
            desc: "Multiply matrices by scalars to produce new matrices, e.g., as when all of the payoffs in a game are doubled.",
          },
          {
            id: "N-VM.C.8",
            s: "C.8",
            desc: "Add, subtract, and multiply matrices of appropriate dimensions.",
          },
          {
            id: "N-VM.C.9",
            s: "C.9",
            desc: "Understand that, unlike multiplication of numbers, matrix multiplication for square matrices is not a commutative operation, but still satisfies the associative and distributive properties.",
          },
        ],
      },
    ],
  },
  {
    id: "S-CP",
    code: "S-CP",
    name: "Conditional Probability & the Rules of Probability",
    grade: "HS",
    category: "Statistics & Probability",
    clusters: [
      {
        id: "S-CP.A",
        letter: "A",
        short:
          "Understand independence and conditional probability and use them to interpret data.",
        standards: [
          {
            id: "S-CP.A.1",
            s: "A.1",
            desc: "Describe events as subsets of a sample space (the set of outcomes) using characteristics (or categories) of the outcomes, or as unions, intersections, or complements of other events (“or,” “and,” “not”).",
          },
          {
            id: "S-CP.A.2",
            s: "A.2",
            desc: "Understand that two events A and B are independent if the probability of A and B occurring together is the product of their probabilities, and use this characterization to determine if they are independent.",
          },
          {
            id: "S-CP.A.3",
            s: "A.3",
            desc: "Understand the conditional probability of A given B as P(A and B)/P(B), and interpret independence of A and B as saying that the conditional probability of A given B is the same as the probability of A, and the conditional probability of B given A is the same as the probability of B.",
          },
          {
            id: "S-CP.A.4",
            s: "A.4",
            desc: "Construct and interpret two-way frequency tables of data when two categories are associated with each object being classified. Use the two-way table as a sample space to decide if events are independent and to approximate conditional probabilities. For example, collect data from a random sample of students in your school on their favorite subject among math, science, and English. Estimate the probability that a randomly selected student from your school will favor science given that the student is in tenth grade. Do the same for other subjects and compare the results.",
          },
          {
            id: "S-CP.A.5",
            s: "A.5",
            desc: "Recognize and explain the concepts of conditional probability and independence in everyday language and everyday situations. For example, compare the chance of having lung cancer if you are a smoker with the chance of being a smoker if you have lung cancer.",
          },
        ],
      },
      {
        id: "S-CP.B",
        letter: "B",
        short:
          "Use the rules of probability to compute probabilities of compound events in a uniform probability model.",
        standards: [
          {
            id: "S-CP.B.6",
            s: "B.6",
            desc: "Find the conditional probability of A given B as the fraction of B’s outcomes that also belong to A, and interpret the answer in terms of the model.",
          },
          {
            id: "S-CP.B.7",
            s: "B.7",
            desc: "Apply the Addition Rule, P(A or B) = P(A) + P(B) – P(A and B), and interpret the answer in terms of the model.",
          },
          {
            id: "S-CP.B.8",
            s: "B.8",
            desc: "Apply the general Multiplication Rule in a uniform probability model, P(A and B) = P(A)P(B|A) = P(B)P(A|B), and interpret the answer in terms of the model.",
          },
          {
            id: "S-CP.B.9",
            s: "B.9",
            desc: "Use permutations and combinations to compute probabilities of compound events and solve problems.",
          },
        ],
      },
    ],
  },
  {
    id: "S-IC",
    code: "S-IC",
    name: "Making Inferences & Justifying Conclusions",
    grade: "HS",
    category: "Statistics & Probability",
    clusters: [
      {
        id: "S-IC.A",
        letter: "A",
        short:
          "Understand and evaluate random processes underlying statistical experiments.",
        standards: [
          {
            id: "S-IC.A.1",
            s: "A.1",
            desc: "Understand statistics as a process for making inferences about population parameters based on a random sample from that population.",
          },
          {
            id: "S-IC.A.2",
            s: "A.2",
            desc: "Decide if a specified model is consistent with results from a given data-generating process, e.g., using simulation. For example, a model says a spinning coin falls heads up with probability 0.5. Would a result of 5 tails in a row cause you to question the model?",
          },
        ],
      },
      {
        id: "S-IC.B",
        letter: "B",
        short:
          "Make inferences and justify conclusions from sample surveys, experiments, and observational studies.",
        standards: [
          {
            id: "S-IC.B.3",
            s: "B.3",
            desc: "Recognize the purposes of and differences among sample surveys, experiments, and observational studies; explain how randomization relates to each.",
          },
          {
            id: "S-IC.B.4",
            s: "B.4",
            desc: "Use data from a sample survey to estimate a population mean or proportion; develop a margin of error through the use of simulation models for random sampling.",
          },
          {
            id: "S-IC.B.5",
            s: "B.5",
            desc: "Use data from a randomized experiment to compare two treatments; use simulations to decide if differences between parameters are significant.",
          },
          {
            id: "S-IC.B.6",
            s: "B.6",
            desc: "Evaluate reports based on data.",
          },
        ],
      },
    ],
  },
  {
    id: "S-ID",
    code: "S-ID",
    name: "Interpreting Categorical & Quantitative Data",
    grade: "HS",
    category: "Statistics & Probability",
    clusters: [
      {
        id: "S-ID.A",
        letter: "A",
        short:
          "Summarize, represent, and interpret data on a single count or measurement variable.",
        standards: [
          {
            id: "S-ID.A.1",
            s: "A.1",
            desc: "Represent data with plots on the real number line (dot plots, histograms, and box plots).",
          },
          {
            id: "S-ID.A.2",
            s: "A.2",
            desc: "Use statistics appropriate to the shape of the data distribution to compare center (median, mean) and spread (interquartile range, standard deviation) of two or more different data sets.",
          },
          {
            id: "S-ID.A.3",
            s: "A.3",
            desc: "Interpret differences in shape, center, and spread in the context of the data sets, accounting for possible effects of extreme data points (outliers).",
          },
          {
            id: "S-ID.A.4",
            s: "A.4",
            desc: "Use the mean and standard deviation of a data set to fit it to a normal distribution and to estimate population percentages. Recognize that there are data sets for which such a procedure is not appropriate. Use calculators, spreadsheets, and tables to estimate areas under the normal curve.",
          },
        ],
      },
      {
        id: "S-ID.B",
        letter: "B",
        short:
          "Summarize, represent, and interpret data on two categorical and quantitative variables.",
        standards: [
          {
            id: "S-ID.B.5",
            s: "B.5",
            desc: "Summarize categorical data for two categories in two-way frequency tables. Interpret relative frequencies in the context of the data (including joint, marginal, and conditional relative frequencies). Recognize possible associations and trends in the data.",
          },
          {
            id: "S-ID.B.6",
            s: "B.6",
            desc: "Represent data on two quantitative variables on a scatter plot, and describe how the variables are related.",
          },
        ],
      },
      {
        id: "S-ID.C",
        letter: "C",
        short: "Interpret linear models.",
        standards: [
          {
            id: "S-ID.C.7",
            s: "C.7",
            desc: "Interpret the slope (rate of change) and the intercept (constant term) of a linear model in the context of the data.",
          },
          {
            id: "S-ID.C.8",
            s: "C.8",
            desc: "Compute (using technology) and interpret the correlation coefficient of a linear fit.",
          },
          {
            id: "S-ID.C.9",
            s: "C.9",
            desc: "Distinguish between correlation and causation.",
          },
        ],
      },
    ],
  },
  {
    id: "S-MD",
    code: "S-MD",
    name: "Using Probability to Make Decisions",
    grade: "HS",
    category: "Statistics & Probability",
    clusters: [
      {
        id: "S-MD.A",
        letter: "A",
        short: "Calculate expected values and use them to solve problems.",
        standards: [
          {
            id: "S-MD.A.1",
            s: "A.1",
            desc: "Define a random variable for a quantity of interest by assigning a numerical value to each event in a sample space; graph the corresponding probability distribution using the same graphical displays as for data distributions.",
          },
          {
            id: "S-MD.A.2",
            s: "A.2",
            desc: "Calculate the expected value of a random variable; interpret it as the mean of the probability distribution.",
          },
          {
            id: "S-MD.A.3",
            s: "A.3",
            desc: "Develop a probability distribution for a random variable defined for a sample space in which theoretical probabilities can be calculated; find the expected value. For example, find the theoretical probability distribution for the number of correct answers obtained by guessing on all five questions of a multiple-choice test where each question has four choices, and find the expected grade under various grading schemes.",
          },
          {
            id: "S-MD.A.4",
            s: "A.4",
            desc: "Develop a probability distribution for a random variable defined for a sample space in which probabilities are assigned empirically; find the expected value. For example, find a current data distribution on the number of TV sets per household in the United States, and calculate the expected number of sets per household. How many TV sets would you expect to find in 100 randomly selected households?",
          },
        ],
      },
      {
        id: "S-MD.B",
        letter: "B",
        short: "Use probability to evaluate outcomes of decisions.",
        standards: [
          {
            id: "S-MD.B.5",
            s: "B.5",
            desc: "Weigh the possible outcomes of a decision by assigning probabilities to payoff values and finding expected values.",
          },
          {
            id: "S-MD.B.6",
            s: "B.6",
            desc: "Use probabilities to make fair decisions (e.g., drawing by lots, using a random number generator).",
          },
          {
            id: "S-MD.B.7",
            s: "B.7",
            desc: "Analyze decisions and strategies using probability concepts (e.g., product testing, medical testing, pulling a hockey goalie at the end of a game).",
          },
        ],
      },
    ],
  },
];
